import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import styled, { css } from 'styled-components';
import Button from './Button.jsx';
import projectService from '../services/projectService.js';
import { useAuth } from '../auth.js';
import AddressAutocomplete from './AddressAutocomplete';
import { useFlexibleTranslation } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import CompanyService from '../services/companyService';
import { phConstructionTypeSet } from '../utils/posthogEvents';

import { OCCUPANCY_TYPES_US } from '../constants/occupancyTypes_US';
import { OCCUPANCY_TYPES_CA } from '../constants/occupancyTypes_CA';
import { CONSTRUCTION_TYPES_CA } from '../constants/constructionTypes_CA';
import { CONSTRUCTION_TYPES_US } from '../constants/constructionTypes_US';
import {
  formatWithCommas,
  removeCommas,
  isPart9Canada,
  PROJECT_PHASE_OPTIONS,
  PROJECT_TYPE_OPTIONS,
} from '../constants/projectFieldsConfig';
import {
  DEFAULT_PROJECT_CATEGORY,
  PROJECT_CATEGORY_OPTIONS,
  getProjectCategoryProfile,
  isCivilCategory,
  isFieldVisible,
} from '../constants/projectCategoryProfiles';
import { ENTITY_DISCIPLINE_OPTIONS, formatEntityDisciplinesLabel } from '../constants/entityDisciplines';
import {
  captureEntityFromFormState,
  createEmptyEntity,
  wizardDefaultEntitiesForCategory,
  entityToFlatProjectMirror,
  entityToFormPatch,
  serializeEntitiesData,
} from '../utils/projectEntityHelpers';
import InfraTypeSelector from './InfraTypeSelector';
import {
  formatInfraTypesLabel,
  getDesignStandardLabel,
  getDesignStandardOptions,
  getInfraMetricsProfile,
  getInfraUnitLabels,
  infraByKey,
  MATERIAL_CONTEXT_OPTIONS,
} from '../constants/infrastructureElementTypes';
import { CIVIL_VERTICAL_OPTIONS, CIVIL_VERTICALS_SELECTION_HINT, formatCivilVerticalsLabel } from '../constants/civilVerticals';
import '../styles/ProjectSettings.css';

const T = {
  orange:          '#e97024',
  orangeDim:       'rgba(233, 112, 36, 0.07)',
  orangeBorder:    'rgba(233, 112, 36, 0.25)',
  orangeRing:      'rgba(233, 112, 36, 0.22)',
  orangeHover:     '#d4621e',
  warnBg:          '#fffbeb',
  warnBorder:      '#f59e0b',
  warnText:        '#92400e',
  bgPage:          '#fafafa',
  bgCard:          '#ffffff',
  bgInput:         '#f5f5f5',
  bgInputHover:    '#ebebeb',
  borderSubtle:    '#f0f0f0',
  borderMedium:    '#e0e0e0',
  textPrimary:     '#333333',
  textSecondary:   '#555555',
  textTertiary:    '#999999',
  shadowModal:     '0 8px 40px rgba(0,0,0,0.18)',
  radiusCard:      '8px',
  radiusInput:     '8px',
  font:            "'Poppins', sans-serif",
  purple:          '#7c3aed',
  purpleDim:       'rgba(124, 58, 237, 0.08)',
  purpleDimHover:  'rgba(124, 58, 237, 0.15)',
};

// ─── Styled Components ───────────────────────────────────────────────────────

const DialogOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.42);
  display: flex; align-items: center; justify-content: center;
  z-index: 2000; padding: 1.5rem; font-family: ${T.font};
`;

const DialogContent = styled.div`
  background: ${T.bgCard}; border-radius: 12px; box-shadow: ${T.shadowModal};
  width: 100%; max-width: 600px; display: flex; flex-direction: column;
  max-height: calc(100vh - 3rem); overflow: visible;
  font-family: ${T.font}; font-size: 0.9rem; color: ${T.textPrimary};
`;

const WizardHeader = styled.div`
  padding: 1.375rem 1.5rem 1.125rem; border-bottom: 1px solid ${T.borderSubtle};
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 1rem; flex-shrink: 0;
`;

const HeaderLeft = styled.div`display: flex; align-items: center; gap: 0.625rem;`;

const WizardIcon = styled.div`
  width: 32px; height: 32px; background: ${T.orangeDim};
  border: 1px solid ${T.orangeBorder}; border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
  color: ${T.orange}; flex-shrink: 0;
`;

const TitleBlock = styled.div``;
const WizardTitle = styled.div`font-size: 0.97rem; font-weight: 700; color: ${T.textPrimary}; line-height: 1.2;`;
const WizardSubtitle = styled.div`font-size: 0.75rem; color: ${T.textTertiary}; margin-top: 2px;`;

const CloseButton = styled.button`
  background: none; border: none; cursor: pointer; color: ${T.textTertiary};
  padding: 4px; border-radius: 4px; line-height: 1; font-size: 1.25rem;
  transition: color 0.15s ease; flex-shrink: 0; align-self: flex-start;
  &:hover { color: ${T.textPrimary}; }
`;

const ProgressWrap = styled.div`padding: 0.875rem 1.5rem 0; flex-shrink: 0;`;
const ProgressHeader = styled.div`display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.375rem;`;
const ProgressLabel = styled.span`font-size: 0.72rem; font-weight: 600; color: ${T.textSecondary}; text-transform: uppercase; letter-spacing: 0.4px;`;
const ProgressPct = styled.span`font-size: 0.72rem; font-weight: 700; color: ${T.orange};`;
const ProgressTrack = styled.div`height: 4px; background: ${T.borderSubtle}; border-radius: 99px; overflow: hidden;`;
const ProgressFill = styled.div`
  height: 100%; background: ${T.orange}; border-radius: 99px;
  transition: width 0.4s cubic-bezier(0.4,0,0.2,1); width: ${p => p.$pct}%;
`;

const WizardBody = styled.div`
  padding: 1.375rem 1.5rem; overflow-y: auto; flex: 1;
  max-height: ${p => p.$step === 2 ? 'none' : 'calc(100vh - 3rem - 80px - 50px - 72px)'};
`;

const WizardFooter = styled.div`
  padding: 1rem 1.5rem; border-top: 1px solid ${T.borderSubtle};
  display: flex; align-items: center; justify-content: space-between;
  flex-shrink: 0; gap: 0.75rem;
`;

const FormGroup = styled.div`margin-bottom: 1.125rem; &:last-child { margin-bottom: 0; }`;

const FieldLabel = styled.label`
  display: block; font-size: 0.78rem; font-weight: 600; color: ${T.textSecondary};
  text-transform: uppercase; letter-spacing: 0.35px; margin-bottom: 0.4rem;
`;

const RequiredMark = styled.span`color: ${T.orange}; margin-left: 2px;`;
const OptionalTag = styled.span`font-size: 0.72rem; font-weight: 400; color: ${T.textTertiary}; text-transform: none; letter-spacing: 0; margin-left: 4px;`;

const InputField = styled.input`
  width: 100%; padding: 0.625rem 0.875rem; font-family: ${T.font};
  font-size: 0.88rem; color: ${T.textPrimary}; background: ${T.bgInput};
  border: none; border-radius: ${T.radiusInput}; outline: none;
  transition: all 0.15s ease; box-sizing: border-box;
  &:hover { background: ${T.bgInputHover}; }
  &:focus { background: #fff; box-shadow: 0 0 0 2px ${T.orangeRing}; }
  &::placeholder { color: ${T.textTertiary}; }
  ${p => p.$error && css`box-shadow: 0 0 0 2px rgba(211,47,47,0.25); background: #fff;`}
`;

const ParamEditInput = styled.input`
  flex: 1; padding: 0.45rem 0.75rem; font-family: ${T.font}; font-size: 0.85rem;
  color: ${T.textPrimary}; background: #fff;
  border: 1px solid ${T.borderMedium}; border-radius: 6px; outline: none;
  box-shadow: 0 0 0 2px ${T.orangeRing};
  &:focus { border-color: ${T.orange}; }
`;

const SelectField = styled.select`
  width: 100%; padding: 0.625rem 0.875rem; font-family: ${T.font};
  font-size: 0.88rem; color: ${p => p.value ? T.textPrimary : T.textTertiary};
  background: ${T.bgInput}; border: none; border-radius: ${T.radiusInput};
  outline: none; cursor: pointer; transition: all 0.15s ease; appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='%23999' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 0.875rem center; padding-right: 2.25rem;
  &:hover { background-color: ${T.bgInputHover}; }
  &:focus { background-color: #fff; box-shadow: 0 0 0 2px ${T.orangeRing}; }
  option { color: ${T.textPrimary}; }
`;

const FormRow = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 0.875rem;`;
const FormRow3 = styled.div`display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0.875rem;`;

const FieldHint = styled.div`font-size: 0.75rem; color: ${T.textTertiary}; margin-top: 0.35rem; line-height: 1.5;`;
const FieldError = styled.div`font-size: 0.75rem; color: #d32f2f; margin-top: 0.35rem;`;

const SectionDivider = styled.hr`border: none; border-top: 1px solid ${T.borderSubtle}; margin: 0.25rem 0 1.375rem;`;

const InfoNote = styled.div`
  display: flex; align-items: flex-start; gap: 0.5rem; margin-top: 0.5rem;
  padding: 0.5rem 0.75rem; background: ${T.bgInput}; border-radius: 6px;
  border-left: 3px solid ${T.borderMedium}; font-size: 0.78rem; color: ${T.textSecondary}; line-height: 1.5;
`;

const WarningNote = styled.div`
  display: flex; align-items: flex-start; gap: 0.5rem; margin-top: 0.5rem;
  padding: 0.5rem 0.75rem; background: ${T.warnBg}; border-radius: 6px;
  border-left: 3px solid ${T.warnBorder}; font-size: 0.78rem; color: ${T.warnText}; line-height: 1.5;
`;

const ReadOnlyDisplay = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.625rem 0.875rem; background: ${T.bgInput};
  border-radius: ${T.radiusInput}; font-size: 0.88rem; font-weight: 600; color: ${T.textPrimary};
`;

const AutoBadge = styled.span`
  font-size: 0.68rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px;
  color: ${T.textTertiary}; background: ${T.borderSubtle}; border-radius: 4px; padding: 2px 7px;
`;

const ManualBadge = styled.button`
  font-size: 0.68rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px;
  color: ${T.purple}; background: ${T.purpleDim}; border-radius: 4px; padding: 2px 7px;
  border: none; cursor: pointer; font-family: ${T.font}; white-space: nowrap;
  &:hover { background: ${T.purpleDimHover}; }
`;

const AreaSplitWrapper = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 0.625rem; margin-bottom: 0.375rem;`;
const SubLabel = styled.div`font-size: 0.72rem; color: ${T.textTertiary}; margin-bottom: 0.3rem; font-weight: 500;`;
const AreaTotal = styled.div`
  font-size: 0.75rem; color: ${T.textSecondary}; padding: 0.375rem 0.75rem;
  background: rgba(37,99,235,0.05); border-radius: 5px; margin-top: 0.25rem;
`;

const OccGroupLabel = styled.div`
  font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
  color: ${T.textTertiary}; margin: 1rem 0 0.4rem; &:first-child { margin-top: 0; }
`;

const OccOption = styled.div`
  display: flex; align-items: flex-start; gap: 10px; padding: 0.55rem 0.875rem;
  cursor: pointer; transition: background 0.12s ease; border-left: 2px solid transparent;
  border-radius: 6px; margin-bottom: 2px;
  background: ${p => p.$selected ? T.orangeDim : 'transparent'};
  border-left-color: ${p => p.$selected ? T.orange : 'transparent'};
  &:hover { background: ${T.orangeDim}; }
`;

const OccCheckbox = styled.div`
  width: 16px; height: 16px; border: 2px solid ${p => p.$checked ? T.orange : T.borderMedium};
  border-radius: 3px; flex-shrink: 0; margin-top: 2px;
  display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 700;
  background: ${p => p.$checked ? T.orange : 'transparent'}; color: #fff; transition: all 0.12s ease;
`;

const OccOptionTitle = styled.div`font-size: 0.82rem; font-weight: 600; color: ${T.textPrimary};`;
const OccOptionCode = styled.span`color: ${T.orange}; font-weight: 700;`;
const OccOptionDesc = styled.div`font-size: 0.73rem; color: ${T.textTertiary}; line-height: 1.4;`;
const SearchInput = styled(InputField)`margin-bottom: 0.875rem;`;

// ── Summary styles ──
const SummarySection = styled.div`margin-bottom: 1.25rem; &:last-child { margin-bottom: 0; }`;
const SumSectionLabel = styled.div`
  font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
  color: ${T.textTertiary}; margin-bottom: 0.5rem;
`;
const ParamRows = styled.div`display: flex; flex-direction: column; gap: 1px;`;

const ParamRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.5rem 0.75rem; background: #fafafa; border-radius: 4px; gap: 0.75rem;
  ${p => p.$editing && css`flex-direction: column; align-items: stretch; padding: 0.625rem 0.75rem;`}
`;

const ParamRowLeft = styled.div`display: flex; flex-direction: column; gap: 0.15rem; flex: 1; min-width: 0;`;
const ParamValueRow = styled.div`display: flex; align-items: center; gap: 0.35rem; justify-content: flex-end; text-align: right;`;

const ParamLabel = styled.span`font-size: 0.78rem; color: ${T.textSecondary}; font-weight: 500; flex-shrink: 0;`;

const ParamValue = styled.span`
  font-size: 0.82rem; color: ${p => p.$missing ? T.textTertiary : T.textPrimary};
  font-weight: ${p => p.$missing ? 400 : 600}; font-style: ${p => p.$missing ? 'italic' : 'normal'};
  text-align: right;
`;

const ParamWarnValue = styled.span`
  font-size: 0.75rem; color: ${T.warnText}; font-weight: 500; font-style: italic;
  text-align: right; max-width: 260px;
`;

const ParamEditControls = styled.div`
  display: flex; align-items: center; gap: 0.5rem; width: 100%;
  ${p => p.$stacked && css`flex-direction: column; align-items: stretch;`}
`;

const ParamEditActions = styled.div`display: flex; gap: 0.5rem; align-items: center; justify-content: flex-end;`;

const PencilBtn = styled.button`
  background: none; border: none; cursor: pointer; color: ${T.textTertiary};
  padding: 2px 4px; border-radius: 4px; line-height: 1; flex-shrink: 0;
  transition: color 0.15s ease;
  &:hover { color: ${T.orange}; }
`;

const ParamSaveBtn = styled.button`
  padding: 0.4rem 0.75rem; background: ${T.orange}; color: #fff; border: none;
  border-radius: 5px; font-family: ${T.font}; font-size: 0.78rem; font-weight: 600;
  cursor: pointer; white-space: nowrap;
  &:hover { background: #d4611a; }
`;

const ParamCancelBtn = styled.button`
  padding: 0.4rem 0.6rem; background: none; color: ${T.textTertiary};
  border: 1px solid ${T.borderMedium}; border-radius: 5px; font-family: ${T.font};
  font-size: 0.78rem; cursor: pointer; white-space: nowrap;
  &:hover { color: ${T.textPrimary}; background: ${T.bgInput}; }
`;

// ── Right-side occ popup (Step 5) ──
const OccPopupRight = styled.div`
  position: fixed; top: 50%; transform: translateY(-50%);
  width: 320px; background: #fff; border: 1px solid ${T.borderMedium};
  border-radius: ${T.radiusInput}; box-shadow: 0 8px 28px rgba(0,0,0,0.15);
  z-index: 2100; display: flex; flex-direction: column; max-height: 480px;
`;

// ── Congrats ──
const CongratulatoryMessage = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; padding: 2rem 1rem;
`;
const CongratulatoryIconRing = styled.div`
  width: 72px; height: 72px; border-radius: 50%; background: ${T.orangeDim};
  border: 2px solid ${T.orangeBorder}; display: flex; align-items: center; justify-content: center;
  color: ${T.orange}; margin-bottom: 1.25rem;
`;
const CongratulatoryTitle = styled.div`font-size: 1.15rem; font-weight: 700; color: ${T.textPrimary}; margin-bottom: 0.5rem;`;
const CongratulatoryBody = styled.div`font-size: 0.88rem; color: ${T.textSecondary}; line-height: 1.6; max-width: 400px;`;

const WizardBtn = styled.button`
  display: inline-flex; align-items: center; gap: 0.4rem; font-family: ${T.font};
  font-size: 0.85rem; font-weight: 600; border-radius: 7px; padding: 0.55rem 1.25rem;
  cursor: pointer; border: none; transition: all 0.15s ease; white-space: nowrap;
  ${p => p.$primary && css`
    background: ${T.orange}; color: #fff; box-shadow: 0 2px 6px rgba(233,112,36,0.3);
    &:hover:not(:disabled) { background: ${T.orangeHover}; box-shadow: 0 4px 12px rgba(233,112,36,0.35); transform: translateY(-1px); }
    &:active { transform: translateY(0); }
    &:disabled { opacity: 0.45; cursor: not-allowed; transform: none; box-shadow: none; }
  `}
  ${p => p.$ghost && css`
    background: transparent; color: ${T.textSecondary}; border: 1px solid ${T.borderMedium};
    &:hover { background: ${T.bgInput}; color: ${T.textPrimary}; }
  `}
`;

// ─── Occ popup (shared) ───────────────────────────────────────────────────────
const OccPopupWrap = styled.div`position: relative;`;
const OccDisplay = styled.div`
  background: ${T.bgInput}; border-radius: ${T.radiusInput};
  padding: 0.48rem 0.875rem; min-height: 38px;
  display: flex; align-items: center; flex-wrap: wrap; gap: 0.35rem;
  cursor: pointer; transition: background 0.14s ease;
  &:hover { background: ${T.bgInputHover}; }
`;
const OccPlaceholder = styled.span`color: ${T.textTertiary}; font-size: 0.875rem;`;
const OccTag = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  background: ${T.orangeDim}; color: ${T.orange}; border: 1px solid ${T.orangeBorder};
  border-radius: 4px; padding: 2px 8px; font-size: 0.78rem; font-weight: 600;
`;
const OccPopupBox = styled.div`
  position: relative;
  background: #fff; border: 1px solid ${T.borderMedium}; border-radius: ${T.radiusInput};
  box-shadow: 0 8px 28px rgba(0,0,0,0.12); z-index: 400; margin-top: 6px;
  display: ${p => p.$open ? 'flex' : 'none'}; flex-direction: column;
`;
const OccPopupHeader = styled.div`padding: 0.75rem 0.875rem 0.5rem; border-bottom: 1px solid ${T.borderSubtle};`;
const OccPopupTitle = styled.div`font-size: 0.78rem; font-weight: 700; color: ${T.textPrimary}; margin-bottom: 2px;`;
const OccPopupSubtitle = styled.div`font-size: 0.72rem; color: ${T.textTertiary};`;
const OccSearchWrap = styled.div`padding: 0.5rem 0.875rem; border-bottom: 1px solid ${T.borderSubtle};`;
const OccSearchInput = styled.input`
  width: 100%; background: ${T.bgInput}; border: none; border-radius: ${T.radiusInput};
  padding: 0.42rem 0.75rem; font-family: ${T.font}; font-size: 0.8rem; outline: none;
  &:focus { background: #fff; box-shadow: 0 0 0 2px ${T.orangeRing}; }
`;
const OccList = styled.div`flex: 1; overflow-y: auto; max-height: 320px;`;

const OccPopupFooter = styled.div`
  padding: 0.5rem 0.875rem; border-top: 1px solid ${T.borderSubtle};
  display: flex; align-items: center; justify-content: space-between; background: #fafafa;
`;
const OccCount = styled.span`font-size: 0.73rem; color: ${T.textTertiary};`;
const OccDoneBtn = styled.button`
  padding: 0.36rem 0.875rem; background: ${T.orange}; color: #fff; border: none;
  border-radius: 4px; font-size: 0.78rem; font-weight: 600; font-family: ${T.font};
  cursor: pointer; &:hover { background: #d4611a; }
`;

// ─── SVG Icons ───────────────────────────────────────────────────────────────
const PencilIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

// ─── Helpers ─────────────────────────────────────────────────────────────────
const STEP_META = {
  1: { label: 'Project Basics',      subtitle: 'Step 1 — Project Basics',      pct: 30  },
  2: { label: 'Occupancy',           subtitle: 'Step 2 — Occupancy',           pct: 50  },
  3: { label: 'Building Parameters', subtitle: 'Step 3 — Building Parameters', pct: 65  },
  4: { label: 'Compliance Context',  subtitle: 'Step 4 — Compliance Context',  pct: 80  },
  5: { label: 'Review & Confirm',    subtitle: 'Step 5 — Review & Confirm',    pct: 95  },
  6: { label: 'Done',                subtitle: 'Project Created',              pct: 100 },
};

const getStepMeta = (step, profile) => {
  if (profile?.civilTwoStepWizard) {
    if (step === 1) {
      return { label: 'Project Basics & Scope', subtitle: 'Step 1 — Project Basics & Civil Scope', pct: 50 };
    }
    if (step === 5) {
      return { label: 'Review & Confirm', subtitle: 'Step 2 — Review & Confirm', pct: 95 };
    }
    if (step === 6) return STEP_META[6];
  }
  const base = STEP_META[step] || STEP_META[1];
  if (!profile || step < 2 || step > 4) return base;
  const labels = {
    2: profile.step2Label || base.label,
    3: profile.step3Label || base.label,
    4: profile.step4Label || base.label,
  };
  if (!labels[step]) return base;
  return {
    ...base,
    label: labels[step],
    subtitle: `Step ${step} — ${labels[step]}`,
  };
};


const getCountryCodeFromAddressComponents = (addressComponents = []) => {
  const c = addressComponents.find((x) => Array.isArray(x.types) && x.types.includes('country'));
  return c?.short_name || null;
};


const getOccupancyGroupLabel = (code, region) => {
  const c = (code || '').toLowerCase();
  if (region === 'CA') {
    if (c.startsWith('a')) return 'Group A — Assembly';
    if (c.startsWith('b')) return 'Group B — Care / Detention';
    if (c === 'c') return 'Group C — Residential';
    if (c === 'd') return 'Group D — Business';
    if (c === 'e') return 'Group E — Mercantile';
    if (c.startsWith('f')) return 'Group F — Industrial';
    return 'Other';
  }
  if (c.startsWith('a')) return 'Group A — Assembly';
  if (c === 'b') return 'Group B — Business';
  if (c === 'e') return 'Group E — Educational';
  if (c === 'm') return 'Group M — Mercantile';
  if (c.startsWith('r')) return 'Group R — Residential';
  if (c.startsWith('s')) return 'Group S — Storage';
  if (c === 'u') return 'Group U — Utility';
  if (c.startsWith('h')) return 'Group H — High Hazard';
  if (c.startsWith('i')) return 'Group I — Institutional';
  return 'Other';
};

const getUnitLabels = (occupancyRegion) => ({
  areaLabel: occupancyRegion === 'CA' ? 'm²' : 'ft²',
  linearLabel: occupancyRegion === 'CA' ? 'm' : 'ft',
});


const SPRINKLER_OPTIONS = [
  { value: 'entire_building', label: 'Entire Building' },
  { value: 'basement_only', label: 'Basement Only' },
  { value: 'in_lieu_of_roof_rating', label: 'In Lieu of Roof Rating' },
  { value: 'unknown', label: 'Unknown' },
  { value: 'not_required', label: 'Not Required' },
];

const YES_NO_UNKNOWN_OPTIONS = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

// ─── Toggle Group ─────────────────────────────────────────────────────────────
const ToggleGroupWrap = styled.div`
  display: inline-flex; border-radius: ${T.radiusInput}; overflow: hidden;
  border: 1px solid ${T.borderMedium};
`;
const ToggleBtn = styled.button`
  padding: 0.48rem 1rem; font-size: 0.8rem; font-weight: 600; font-family: ${T.font};
  background: ${p => p.$active ? T.orange : 'transparent'}; border: none;
  cursor: pointer; color: ${p => p.$active ? '#fff' : T.textSecondary};
  transition: all 0.13s ease; white-space: nowrap;
  &:not(:last-child) { border-right: 1px solid ${T.borderMedium}; }
  &:hover { background: ${p => p.$active ? T.orange : T.bgInputHover}; color: ${p => p.$active ? '#fff' : T.textPrimary}; }
`;

function ToggleGroup({ value, options, onChange }) {
  return (
    <ToggleGroupWrap>
      {options.map(opt => (
        <ToggleBtn key={opt.value} $active={value === opt.value} type="button" onClick={() => onChange(opt.value)}>
          {opt.label}
        </ToggleBtn>
      ))}
    </ToggleGroupWrap>
  );
}

// ─── Classification options helper ───────────────────────────────────────────
const getClassificationOptions = (region) =>
  region === 'CA'
    ? [{ value: 'Part 9', label: 'Part 9 — Small Building (NBC/OBC)' }, { value: 'Part 3', label: 'Part 3 — Large Building (NBC/OBC)' }]
    : [{ value: 'IBC (US)', label: 'IBC (US)' }];

// ─── Main Component ───────────────────────────────────────────────────────────
const ProjectWizard = ({ isOpen, onClose, onProjectCreated }) => {
  const auth = useAuth();
  const { t } = useFlexibleTranslation();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);
  const wizardRef = useRef(null);
  const regionDidMount = useRef(false);

  const [occupancyRegion, setOccupancyRegion] = useState('CA');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchQueryRight, setSearchQueryRight] = useState('');
  const [nameError, setNameError] = useState('');
  const [classificationOverride, setClassificationOverride] = useState(null);

  // Step 2 classification edit state
  const [classStep2Editing, setClassStep2Editing] = useState(false);
  const [classStep2Draft, setClassStep2Draft] = useState('');

  // Step 5 inline edit state — which field is open
  const [editingField, setEditingField] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  // Step 5 right-side occ popup
  const [occRightOpen, setOccRightOpen] = useState(false);
  const occRightRef = useRef(null);
  const [occStep2Open, setOccStep2Open] = useState(false);
  const occStep2Ref = useRef(null);

  const [formData, setFormData] = useState({
    name: '', projectType: '', projectPhase: '',
    types: [], occupancyCodes: [], majorOccupancy: '',
    storeysAboveGrade: '', storeysBelowGrade: '', adjacentStreets: '',
    buildingFootprintArea: '', buildingFootprintAreaExisting: '', buildingFootprintAreaProposed: '',
    grossFloorArea: '', grossFloorAreaExisting: '', grossFloorAreaProposed: '',
    buildingHeight: '', highBuilding: '', siteArea: '', averageGrade: '',
    constructionType: '', sprinklerSystem: 'unknown', fireAlarm: 'unknown', standpipe: 'unknown',
    hazardousSubstances: 'no', roughConstructionBudget: '',
    address: '', addressSelected: false, location: { lat: 43.6532, lng: -79.3832 },
  });

  const [projectCategory, setProjectCategory] = useState(DEFAULT_PROJECT_CATEGORY);
  const [entities, setEntities] = useState(() => wizardDefaultEntitiesForCategory(DEFAULT_PROJECT_CATEGORY));
  const [activeEntityIdx, setActiveEntityIdx] = useState(0);
  const [entityMeta, setEntityMeta] = useState({ name: 'Primary Building', structureKind: 'building' });

  const categoryProfile = useMemo(
    () => getProjectCategoryProfile(projectCategory),
    [projectCategory]
  );

  const isAreaSplit = useMemo(
    () => formData.projectType === 'addition' || formData.projectType === 'change_of_use',
    [formData.projectType]
  );

  const effectiveFootprintArea = useMemo(() => {
    if (isAreaSplit) {
      const e = parseFloat(formData.buildingFootprintAreaExisting || '') || 0;
      const p = parseFloat(formData.buildingFootprintAreaProposed || '') || 0;
      return e + p > 0 ? String(Math.round((e + p) * 100) / 100) : '';
    }
    return formData.buildingFootprintArea;
  }, [isAreaSplit, formData.buildingFootprintArea, formData.buildingFootprintAreaExisting, formData.buildingFootprintAreaProposed]);

  const { areaLabel, linearLabel, longLinearLabel, largeAreaLabel } = getInfraUnitLabels(occupancyRegion);

  const CONSTRUCTION_LIST = useMemo(
    () => (occupancyRegion === 'CA' ? CONSTRUCTION_TYPES_CA : CONSTRUCTION_TYPES_US),
    [occupancyRegion]
  );

  const constructionByKey = useMemo(() => {
    const m = new Map();
    for (const [key, title, desc] of CONSTRUCTION_LIST) m.set(key, { key, title, desc });
    return m;
  }, [CONSTRUCTION_LIST]);

  const OCCUPANCY_LIST = useMemo(
    () => (occupancyRegion === 'CA' ? OCCUPANCY_TYPES_CA : OCCUPANCY_TYPES_US),
    [occupancyRegion]
  );

  const occupancyByKey = useMemo(() => {
    const m = new Map();
    for (const [key, title, desc, code] of OCCUPANCY_LIST) m.set(key, { key, title, desc, code });
    return m;
  }, [OCCUPANCY_LIST]);

  const filteredOccupancies = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return OCCUPANCY_LIST.filter(([key, title, desc, code]) =>
      title.toLowerCase().includes(q) || desc.toLowerCase().includes(q) || code.toLowerCase().includes(q)
    );
  }, [searchQuery, OCCUPANCY_LIST]);

  const filteredOccupanciesRight = useMemo(() => {
    const q = searchQueryRight.toLowerCase();
    return OCCUPANCY_LIST.filter(([key, title, desc, code]) =>
      !q || title.toLowerCase().includes(q) || desc.toLowerCase().includes(q) || code.toLowerCase().includes(q)
    );
  }, [searchQueryRight, OCCUPANCY_LIST]);

  const groupedOccupancies = useMemo(() => {
    const groups = {};
    filteredOccupancies.forEach(([key, title, desc, code]) => {
      const group = getOccupancyGroupLabel(code, occupancyRegion);
      if (!groups[group]) groups[group] = [];
      groups[group].push([key, title, desc, code]);
    });
    return groups;
  }, [filteredOccupancies, occupancyRegion]);

  const buildingClassification = useMemo(() => {
    if (occupancyRegion !== 'CA') return 'IBC (US)';
    return isPart9Canada({
      storeysAboveGrade: formData.storeysAboveGrade,
      buildingFootprintArea: effectiveFootprintArea,
      occupancyCodes: formData.occupancyCodes,
    }) ? 'Part 9' : 'Part 3';
  }, [occupancyRegion, formData.storeysAboveGrade, effectiveFootprintArea, formData.occupancyCodes]);

  // The effective classification shown everywhere
  const effectiveClassification = classificationOverride || buildingClassification;

  const getWizardCaptureState = () => ({
    entityMeta,
    projectCategory,
    occupancyTypes: formData.types,
    occupancyCodes: formData.occupancyCodes,
    majorOccupancy: formData.majorOccupancy,
    classificationOverride,
    occupancyRegion,
    storeysAboveGrade: formData.storeysAboveGrade,
    buildingFootprintArea: formData.buildingFootprintArea,
    grossFloorArea: formData.grossFloorArea,
    storeysBelowGrade: formData.storeysBelowGrade,
    averageGrade: formData.averageGrade,
    adjacentStreets: formData.adjacentStreets,
    buildingHeight: formData.buildingHeight,
    constructionType: formData.constructionType,
    highBuilding: formData.highBuilding === 'yes',
    sprinklerSystem: formData.sprinklerSystem,
    fireAlarm: formData.fireAlarm,
    standpipe: formData.standpipe,
    hazardousSubstances: formData.hazardousSubstances === 'yes',
    buildingFootprintAreaExisting: formData.buildingFootprintAreaExisting,
    buildingFootprintAreaProposed: formData.buildingFootprintAreaProposed,
    grossFloorAreaExisting: formData.grossFloorAreaExisting,
    grossFloorAreaProposed: formData.grossFloorAreaProposed,
    isAreaSplit,
  });

  const commitActiveEntityToList = (list, idx) => {
    const captured = captureEntityFromFormState(getWizardCaptureState());
    const next = [...(list || [])];
    const prev = next[idx] || {};
    next[idx] = {
      ...prev,
      ...captured,
      id: prev.id || captured.id,
      name: entityMeta?.name || prev.name || captured.name,
      structureKind: entityMeta?.structureKind || prev.structureKind || captured.structureKind,
      infraType: entityMeta?.infraType ?? prev.infraType ?? '',
      infraTypes: entityMeta?.infraTypes ?? prev.infraTypes ?? [],
      infraTypeCodes: entityMeta?.infraTypeCodes ?? prev.infraTypeCodes ?? [],
      primaryInfraType: entityMeta?.primaryInfraType ?? prev.primaryInfraType ?? '',
      infraFamily: entityMeta?.infraFamily ?? prev.infraFamily ?? '',
      scope: entityMeta?.scope ?? prev.scope ?? '',
      designStandard: entityMeta?.designStandard ?? prev.designStandard ?? '',
      linearExtent: entityMeta?.linearExtent ?? prev.linearExtent ?? '',
      area: entityMeta?.area ?? prev.area ?? '',
      rowWidth: entityMeta?.rowWidth ?? prev.rowWidth ?? '',
      networkDiameter: entityMeta?.networkDiameter ?? prev.networkDiameter ?? '',
      burialDepth: entityMeta?.burialDepth ?? prev.burialDepth ?? '',
      materialContext: entityMeta?.materialContext ?? prev.materialContext ?? '',
      environmentalFloodplain: entityMeta?.environmentalFloodplain ?? prev.environmentalFloodplain ?? 'unknown',
      environmentalSwm: entityMeta?.environmentalSwm ?? prev.environmentalSwm ?? 'unknown',
      civilVerticals: entityMeta?.civilVerticals ?? prev.civilVerticals ?? [],
      scopeOfWork: entityMeta?.scopeOfWork ?? prev.scopeOfWork ?? '',
      disciplines: entityMeta?.disciplines || prev.disciplines || [],
    };
    return next;
  };

  const switchEntityTab = (idx) => {
    if (idx === activeEntityIdx || idx < 0 || idx >= entities.length) return;
    const updated = commitActiveEntityToList(entities, activeEntityIdx);
    setEntities(updated);
    setActiveEntityIdx(idx);
    applyWizardEntityPatch(entityToFormPatch(updated[idx]));
  };

  const applyWizardEntityPatch = (patch) => {
    if (!patch) return;
    setEntityMeta(patch.entityMeta || { name: 'Primary Building', structureKind: 'building' });
    setFormData((prev) => ({
      ...prev,
      types: patch.occupancyTypes || [],
      occupancyCodes: patch.occupancyCodes || [],
      majorOccupancy: patch.majorOccupancy || '',
      storeysAboveGrade: patch.storeysAboveGrade ?? '',
      storeysBelowGrade: patch.storeysBelowGrade ?? '',
      buildingFootprintArea: patch.buildingFootprintArea ?? '',
      buildingFootprintAreaExisting: patch.buildingFootprintAreaExisting ?? '',
      buildingFootprintAreaProposed: patch.buildingFootprintAreaProposed ?? '',
      grossFloorAreaExisting: patch.grossFloorAreaExisting ?? '',
      grossFloorAreaProposed: patch.grossFloorAreaProposed ?? '',
      grossFloorArea: patch.grossFloorArea ?? '',
      averageGrade: patch.averageGrade ?? '',
      adjacentStreets: patch.adjacentStreets ?? '',
      buildingHeight: patch.buildingHeight ?? '',
      constructionType: patch.constructionType || '',
      highBuilding: patch.highBuilding ? 'yes' : (patch.highBuilding === false ? 'no' : prev.highBuilding),
      sprinklerSystem: patch.sprinklerSystem || prev.sprinklerSystem,
      fireAlarm: patch.fireAlarm || prev.fireAlarm,
      standpipe: patch.standpipe || prev.standpipe,
      hazardousSubstances: patch.hazardousSubstances ? 'yes' : 'no',
    }));
    setClassificationOverride(patch.classificationOverride ?? null);
  };

  const handleCategoryChange = (nextCategory) => {
    if (!nextCategory || nextCategory === projectCategory) return;
    const prof = getProjectCategoryProfile(nextCategory);
    const defaults = wizardDefaultEntitiesForCategory(nextCategory);
    setProjectCategory(nextCategory);
    setEntities(defaults);
    setActiveEntityIdx(0);
    applyWizardEntityPatch(entityToFormPatch(defaults[0]));
    if (!isFieldVisible(prof, 'occupancy')) {
      setFormData((prev) => ({ ...prev, types: [], occupancyCodes: [], majorOccupancy: '' }));
      setClassificationOverride(null);
    }
  };

  const handleAddEntity = () => {
    const prof = getProjectCategoryProfile(projectCategory);
    if (!prof.showAddButton) return;
    const updated = commitActiveEntityToList(entities, activeEntityIdx);
    const ent = createEmptyEntity(
      { name: `${prof.entityNameLabel?.replace(/ Name$/i, '') || 'Entity'} ${updated.length + 1}`, sortOrder: updated.length },
      prof
    );
    updated.push(ent);
    setEntities(updated);
    setActiveEntityIdx(updated.length - 1);
    applyWizardEntityPatch(entityToFormPatch(ent));
  };

  const toggleEntityDiscipline = (disciplineId) => {
    setEntityMeta((prev) => {
      const cur = prev.disciplines || [];
      const next = cur.includes(disciplineId)
        ? cur.filter((x) => x !== disciplineId)
        : [...cur, disciplineId];
      return { ...prev, disciplines: next };
    });
  };

  const renderEntityTabs = () => {
    if (!categoryProfile.showTabs || entities.length === 0) return null;
    const addLabel = categoryProfile.addEntityButtonLabel || '+ Add Additional Building';
    return (
      <div className="entity-tabs-bar">
        {entities.length > 1 && entities.map((ent, idx) => (
          <button
            key={ent.id || idx}
            type="button"
            className={`entity-tab${activeEntityIdx === idx ? ' active' : ''}`}
            onClick={() => switchEntityTab(idx)}
          >
            {ent.name || `${categoryProfile.entityNameLabel} ${idx + 1}`}
          </button>
        ))}
        {categoryProfile.showAddButton && (
          <button type="button" className="entity-tab entity-tab-add" onClick={handleAddEntity}>
            {addLabel}
          </button>
        )}
      </div>
    );
  };

  const renderInfraMetricsFields = (metricsOptional = false) => {
    const profile = getInfraMetricsProfile({
      primaryInfraType: entityMeta?.primaryInfraType,
      infraTypes: entityMeta?.infraTypes,
      infraFamily: entityMeta?.infraFamily,
    });
    if (metricsOptional) {
      profile.requireLinearExtent = false;
      profile.requireArea = false;
    }
    const numChange = (field) => (e) => {
      const r = e.target.value;
      if (!/^\d*\.?\d*$/.test(r)) return;
      setEntityMeta((prev) => ({ ...prev, [field]: r }));
    };
    return (
      <>
        {metricsOptional && (
          <InfoNote>
            Metrics are optional during setup. Add design parameters per element now or later in Project Settings — civil projects often define these as documents are linked.
          </InfoNote>
        )}
        {profile.showLinearExtent && (
          <FormGroup>
            <FieldLabel>
              Linear Extent ({profile.family === 'linear' ? longLinearLabel : linearLabel})
              {profile.requireLinearExtent && <RequiredMark>*</RequiredMark>}
            </FieldLabel>
            <InputField
              type="text"
              value={entityMeta?.linearExtent ?? ''}
              onChange={numChange('linearExtent')}
              placeholder={profile.family === 'linear' ? 'e.g. 0.5' : 'e.g. 120'}
            />
          </FormGroup>
        )}
        {profile.showRowWidth && (
          <FormGroup>
            <FieldLabel>ROW Width ({linearLabel})</FieldLabel>
            <InputField type="text" value={entityMeta?.rowWidth ?? ''} onChange={numChange('rowWidth')} placeholder="e.g. 12" />
          </FormGroup>
        )}
        {profile.showArea && (
          <FormGroup>
            <FieldLabel>
              Area ({areaLabel})
              {profile.requireArea && <RequiredMark>*</RequiredMark>}
            </FieldLabel>
            <InputField type="text" value={entityMeta?.area ?? ''} onChange={numChange('area')} placeholder="e.g. 2500" />
            <FieldHint>Large sites may also be expressed in {largeAreaLabel}.</FieldHint>
          </FormGroup>
        )}
        {profile.showNetworkDiameter && (
          <FormRow>
            <FormGroup>
              <FieldLabel>Pipe Diameter ({linearLabel})</FieldLabel>
              <InputField type="text" value={entityMeta?.networkDiameter ?? ''} onChange={numChange('networkDiameter')} placeholder="e.g. 0.3" />
            </FormGroup>
            <FormGroup>
              <FieldLabel>Burial Depth ({linearLabel})</FieldLabel>
              <InputField type="text" value={entityMeta?.burialDepth ?? ''} onChange={numChange('burialDepth')} placeholder="e.g. 1.2" />
            </FormGroup>
          </FormRow>
        )}
        <FormGroup>
          <FieldLabel>Average Grade ({linearLabel})</FieldLabel>
          <InputField type="text" value={formData.averageGrade} name="averageGrade"
            onChange={(e) => { const r = e.target.value; if (!/^\d*\.?\d*$/.test(r)) return; setFormData((p) => ({ ...p, averageGrade: r })); }}
            placeholder="e.g. 2.5" />
        </FormGroup>
      </>
    );
  };

  const handleInfraTypeChange = (patch) => {
    setEntityMeta((prev) => ({ ...prev, ...patch }));
  };

  const renderEntityNameFields = () => {
    if (!isFieldVisible(categoryProfile, 'elementName')) return null;
    return (
      <FormGroup>
        <FieldLabel>{categoryProfile.entityNameLabel}<RequiredMark>*</RequiredMark></FieldLabel>
        <InputField
          type="text"
          value={entityMeta?.name || ''}
          onChange={(e) => setEntityMeta((prev) => ({ ...prev, name: e.target.value }))}
          placeholder={`Enter ${categoryProfile.entityNameLabel?.toLowerCase() || 'name'}`}
        />
      </FormGroup>
    );
  };

  // ── Effects ──
  useEffect(() => {
    const handleError = (e) => console.error('Window error:', e.error);
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);
  
  useEffect(() => {
    if (!occStep2Open) return;
    const handler = (e) => {
      if (occStep2Ref.current && !occStep2Ref.current.contains(e.target)) setOccStep2Open(false);
    };
    setTimeout(() => document.addEventListener('click', handler, true), 0);
    return () => document.removeEventListener('click', handler, true);
  }, [occStep2Open]);

  useEffect(() => {
    const handler = (e) => console.error('Unhandled rejection:', e.reason);
    window.addEventListener('unhandledrejection', handler);
    return () => window.removeEventListener('unhandledrejection', handler);
  }, []);

  useEffect(() => {
    if (formRef.current) formRef.current.scrollTop = 0;
    setEditingField(null);
  }, [step]);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, types: [], occupancyCodes: [], majorOccupancy: '', constructionType: '' }));
    setSearchQuery('');
    setClassificationOverride(null);
    setClassStep2Editing(false);
    if (!regionDidMount.current) {
      regionDidMount.current = true;
      return;
    }
    setEntities((prev) => prev.map((ent) => ({
      ...ent,
      occupancyTypes: [],
      occupancyCodes: [],
      majorOccupancy: '',
      constructionType: '',
      classificationOverride: null,
    })));
  }, [occupancyRegion]);

  useEffect(() => {
    if (!occRightOpen) return;
    const handler = (e) => {
      if (occRightRef.current && !occRightRef.current.contains(e.target)) setOccRightOpen(false);
    };
    setTimeout(() => document.addEventListener('click', handler, true), 0);
    return () => document.removeEventListener('click', handler, true);
  }, [occRightOpen]);

  // ── Handlers ──
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddressSelect = (addressData) => {
    setFormData((prev) => ({ ...prev, address: addressData.address, addressSelected: true, location: addressData.location }));
    setOccupancyRegion(addressData.countryCode === 'CA' ? 'CA' : 'US');
  };

  const toggleCivilVertical = (verticalKey) => {
    setEntityMeta((prev) => {
      const cur = prev.civilVerticals || [];
      const next = cur.includes(verticalKey)
        ? cur.filter((k) => k !== verticalKey)
        : [...cur, verticalKey];
      return { ...prev, civilVerticals: next };
    });
  };

  const handleTypeSelect = (key, code) => {
    setFormData((prev) => {
      const isSelected = prev.types.includes(key);
      const nextTypes = isSelected ? prev.types.filter((k) => k !== key) : [...prev.types, key];
      const nextCodes = isSelected
        ? (prev.occupancyCodes || []).filter((c) => c !== code)
        : [...(prev.occupancyCodes || []), code];
      return { ...prev, types: nextTypes, occupancyCodes: nextCodes, majorOccupancy: nextTypes.length === 1 ? nextTypes[0]
          : (prev.majorOccupancy === key ? '' : prev.majorOccupancy) };
    });
  };

  const handleNext = () => {
    if (categoryProfile.civilTwoStepWizard && step === 1) {
      setEntities(commitActiveEntityToList(entities, activeEntityIdx));
      setStep(5);
      return;
    }
    setStep((prev) => prev + 1);
  };
  const handleBack = () => {
    if (categoryProfile.civilTwoStepWizard && step === 5) {
      setStep(1);
      return;
    }
    setStep((prev) => prev - 1);
  };
  const handleKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); return false; } };

  const handleNameChange = async (e) => {
    const newName = e.target.value;
    setFormData((prev) => ({ ...prev, name: newName }));
    if (newName.trim() === '') { setNameError(''); return; }
    try {
      const existing = await projectService.getProjectsByName(auth, newName);
      setNameError(existing?.length > 0 ? t('project.nameAlreadyExists', 'Project name already exists. Please choose a different name.') : '');
    } catch { setNameError(''); }
  };

  // ── Step 2 classification edit ──
  const startClassStep2Edit = () => {
    setClassStep2Draft(effectiveClassification);
    setClassStep2Editing(true);
  };
  const saveClassStep2 = () => {
    if (classStep2Draft) setClassificationOverride(classStep2Draft);
    setClassStep2Editing(false);
  };
  const cancelClassStep2 = () => setClassStep2Editing(false);
  const clearClassOverride = () => { setClassificationOverride(null); setClassStep2Editing(false); };

  // ── Step 5 inline edit helpers ──
  const startEdit = (field, initialVal) => {
    setEditingField(field);
    setEditDraft((prev) => ({ ...prev, [field]: initialVal ?? '' }));
  };
  const cancelEdit = () => setEditingField(null);

  const saveEditText = (field, srcKey) => {
    const val = (editDraft[field] || '').trim();
    if (srcKey) setFormData((prev) => ({ ...prev, [srcKey]: val }));
    setEditingField(null);
  };

  const saveEditDropdown = (field, srcKey) => {
    const val = editDraft[field];
    if (val !== undefined && srcKey) {
      setFormData((prev) => {
        const next = { ...prev, [srcKey]: val };
        const wasSplit = prev.projectType === 'addition' || prev.projectType === 'change_of_use';
        const nowSplit = val === 'addition' || val === 'change_of_use';
        if (field === 'projectType' && wasSplit && !nowSplit) {
          const fp = Math.round(((parseFloat(prev.buildingFootprintAreaExisting || '') || 0) + (parseFloat(prev.buildingFootprintAreaProposed || '') || 0)) * 100) / 100;
          const gfa = Math.round(((parseFloat(prev.grossFloorAreaExisting || '') || 0) + (parseFloat(prev.grossFloorAreaProposed || '') || 0)) * 100) / 100;
          next.buildingFootprintArea = fp > 0 ? String(fp) : prev.buildingFootprintArea;
          next.grossFloorArea = gfa > 0 ? String(gfa) : prev.grossFloorArea;
        }
        return next;
      });
    }
    setEditingField(null);
  };

  const saveEditNumber = (field, srcKey) => {
    const val = editDraft[field];
    if (val !== undefined && srcKey) setFormData((prev) => ({ ...prev, [srcKey]: val }));
    setEditingField(null);
  };

  const saveEditNumberRequired = (field, srcKey) => {
    const val = (editDraft[field] ?? '').toString().trim();
    if (!val) return; 
    setFormData((prev) => ({ ...prev, [srcKey]: val }));
    setEditingField(null);
  };

  const saveEditClassification = () => {
    if (editDraft['classification']) setClassificationOverride(editDraft['classification']);
    setEditingField(null);
  };

  const clearClassificationFromSummary = () => {
    setClassificationOverride(null);
    setEditingField(null);
  };

  // ── Submit ──
  const handleSubmit = async (e) => {
    try {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      if (isSubmitting) return;
      setIsSubmitting(true);
      if (!auth.isAuthenticated) { nav('/auth/signup'); return; }
      if (!formData.name) { alert(t('project.pleaseProvideProjectName', 'Please provide a project name')); setIsSubmitting(false); return; }

      if (isFieldVisible(categoryProfile, 'storeysAboveGrade')) {
        if (!formData.storeysAboveGrade || !formData.adjacentStreets) {
          alert('Storeys above grade and adjacent streets are required.');
          setIsSubmitting(false);
          return;
        }
        const hasFootprint = isAreaSplit
          ? (formData.buildingFootprintAreaExisting || formData.buildingFootprintAreaProposed)
          : formData.buildingFootprintArea;
        if (!hasFootprint) {
          alert('Building footprint area is required.');
          setIsSubmitting(false);
          return;
        }
      }

      const resolvedFootprint = isAreaSplit
        ? ((parseFloat(formData.buildingFootprintAreaExisting || '') || 0) + (parseFloat(formData.buildingFootprintAreaProposed || '') || 0)) || null
        : formData.buildingFootprintArea ? parseFloat(formData.buildingFootprintArea) : null;

      const resolvedGFA = isAreaSplit
        ? ((parseFloat(formData.grossFloorAreaExisting || '') || 0) + (parseFloat(formData.grossFloorAreaProposed || '') || 0)) || null
        : formData.grossFloorArea ? parseFloat(formData.grossFloorArea) : null;

      let buildingClassificationValue = '';
      if (occupancyRegion === 'CA') {
        buildingClassificationValue = isPart9Canada({
          storeysAboveGrade: formData.storeysAboveGrade,
          buildingFootprintArea: resolvedFootprint ? String(resolvedFootprint) : '',
          occupancyCodes: formData.occupancyCodes,
        }) ? 'Part 9' : 'Part 3';
      }

      const myCompany = await CompanyService.getMyCompany();
      if (!myCompany?.company?.id) throw new Error('User has no company');

      const finalEntities = commitActiveEntityToList(entities, activeEntityIdx);
      const mirror = entityToFlatProjectMirror(finalEntities[0], occupancyRegion);

      const projectData = {
        ...formData,
        companyId: myCompany.company.id, status: 'active', occupancyRegion,
        occupancyTypes: mirror.occupancyTypes?.length ? mirror.occupancyTypes : formData.types,
        occupancyCodes: mirror.occupancyCodes?.length ? mirror.occupancyCodes : formData.occupancyCodes,
        majorOccupancy: mirror.majorOccupancy || formData.majorOccupancy,
        buildingFootprintArea: mirror.buildingFootprintArea ?? resolvedFootprint,
        buildingFootprintAreaExisting: mirror.buildingFootprintAreaExisting,
        buildingFootprintAreaProposed: mirror.buildingFootprintAreaProposed,
        grossFloorAreaExisting: mirror.grossFloorAreaExisting,
        grossFloorAreaProposed: mirror.grossFloorAreaProposed,
        grossFloorArea: mirror.grossFloorArea ?? resolvedGFA,
        siteArea: formData.siteArea ? parseFloat(formData.siteArea) : null,
        storeysAboveGrade: mirror.storeysAboveGrade ?? (formData.storeysAboveGrade ? parseInt(formData.storeysAboveGrade, 10) : null),
        storeysBelowGrade: mirror.storeysBelowGrade ?? (formData.storeysBelowGrade ? parseInt(formData.storeysBelowGrade, 10) : null),
        buildingHeight: mirror.buildingHeight ?? (formData.buildingHeight ? parseFloat(formData.buildingHeight) : null),
        averageGrade: mirror.averageGrade ?? (formData.averageGrade ? parseFloat(formData.averageGrade) : null),
        adjacentStreets: mirror.adjacentStreets ?? (formData.adjacentStreets ? parseInt(formData.adjacentStreets, 10) : null),
        roughConstructionBudget: formData.roughConstructionBudget ? parseFloat(removeCommas(formData.roughConstructionBudget)) : null,
        buildingClassification: mirror.buildingClassification || classificationOverride || buildingClassificationValue || 'IBC (US)',
        constructionType: mirror.constructionType || formData.constructionType || '',
        highBuilding: mirror.highBuilding ?? formData.highBuilding === 'yes',
        sprinklerSystem: mirror.sprinklerSystem || formData.sprinklerSystem || null,
        fireAlarm: mirror.fireAlarm || formData.fireAlarm || null,
        standpipe: mirror.standpipe || formData.standpipe || null,
        hazardousSubstances: mirror.hazardousSubstances ?? formData.hazardousSubstances === 'yes',
        projectCategory,
        entitiesData: serializeEntitiesData(finalEntities),
        lastModified: new Date().toISOString(), startDate: new Date().toISOString(),
        estimatedCompletion: null, type: formData.projectType || '',
        location: formData.address || '', phase: formData.projectPhase || '',
        description: isCivilCategory(projectCategory) ? (entityMeta?.scopeOfWork?.trim() || '') : undefined,
      };

      const newProject = await projectService.createProject(projectData, { isAuthenticated: auth.isAuthenticated, user: auth.user });
      if (!newProject) throw new Error('Failed to create project: No project data returned');
      if (formData.constructionType) {
        phConstructionTypeSet({ projectId: newProject.id, method: 'wizard' });
      }
      if (typeof onProjectCreated === 'function') await onProjectCreated(newProject);
      setStep(6);
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      if (error.message?.includes('Please log in')) { nav('/auth/signup'); }
      else { alert(t('project.failedToCreateProject', `Failed to create project: ${error.message || 'Unknown error'}\n\nPlease try again or contact support.`)); }
    } finally { setIsSubmitting(false); }
  };

  const entityFailsStep = (ent, s) => {
    if (s === 2) {
      if (isFieldVisible(categoryProfile, 'occupancy')) {
        if ((ent.occupancyTypes?.length || 0) === 0) return true;
        if ((ent.occupancyTypes?.length || 0) > 1 && !ent.majorOccupancy) return true;
      }
      return false;
    }
    if (s === 3) {
      if (!isFieldVisible(categoryProfile, 'storeysAboveGrade') && !isFieldVisible(categoryProfile, 'buildingFootprintArea')) return false;
      if (!(ent.storeysAboveGrade ?? '').toString().trim()) return true;
      if (!(ent.adjacentStreets ?? '').toString().trim()) return true;
      const hasFootprint = (ent.buildingFootprintArea ?? '').toString().trim()
        || (ent.buildingFootprintAreaExisting ?? '').toString().trim()
        || (ent.buildingFootprintAreaProposed ?? '').toString().trim();
      if (!hasFootprint) return true;
      return false;
    }
    if (s === 4) {
      if (isFieldVisible(categoryProfile, 'constructionType') && !ent.constructionType) return true;
      return false;
    }
    return false;
  };

  const isNextDisabled = useMemo(() => {
    if (step >= 2 && step <= 4 && entities.length > 1 && !categoryProfile.civilTwoStepWizard) {
      const all = commitActiveEntityToList(entities, activeEntityIdx);
      if (all.some(ent => entityFailsStep(ent, step))) return true;
    }
    if (step === 1) {
      if (nameError || !formData.name.trim() || !formData.addressSelected || !formData.projectType) return true;
      if (categoryProfile.civilTwoStepWizard && !(entityMeta?.civilVerticals?.length)) return true;
      return false;
    }
    if (step === 2) {
      if (isFieldVisible(categoryProfile, 'elementName') && !(entityMeta?.name || '').trim()) return true;
      if (isFieldVisible(categoryProfile, 'occupancy')) {
        if ((formData.types?.length || 0) === 0) return true;
        if (formData.types.length > 1 && !formData.majorOccupancy) return true;
      }
      if (isFieldVisible(categoryProfile, 'infraTypes')) {
        if ((entityMeta?.infraTypes?.length || 0) === 0) return true;
        if (entityMeta.infraTypes.length > 1 && !entityMeta.primaryInfraType) return true;
      }
      return false;
    }
    if (step === 3) {
      if (isFieldVisible(categoryProfile, 'linearExtent') || isFieldVisible(categoryProfile, 'area')) {
        if (categoryProfile.wizardMetricsRequired === false) return false;
        const metrics = getInfraMetricsProfile({
          primaryInfraType: entityMeta?.primaryInfraType,
          infraTypes: entityMeta?.infraTypes,
          infraFamily: entityMeta?.infraFamily,
        });
        if (metrics.requireLinearExtent && !entityMeta?.linearExtent) return true;
        if (metrics.requireArea && !entityMeta?.area) return true;
        return false;
      }
      if (!isFieldVisible(categoryProfile, 'storeysAboveGrade') && !isFieldVisible(categoryProfile, 'buildingFootprintArea')) {
        return false;
      }
      const hasStoreys = !!formData.storeysAboveGrade;
      const hasStreets = !!formData.adjacentStreets;
      const hasFootprint = isAreaSplit
        ? !!(formData.buildingFootprintAreaExisting || formData.buildingFootprintAreaProposed)
        : !!formData.buildingFootprintArea;
      return !hasStoreys || !hasStreets || !hasFootprint;
    }
    if (step === 4) {
      if (isFieldVisible(categoryProfile, 'materialContext') && categoryProfile.wizardComplianceRequired !== false) {
        if (!entityMeta?.materialContext) return true;
      }
      if (!isFieldVisible(categoryProfile, 'constructionType')) return false;
      return !formData.constructionType;
    }
    return false;
  }, [step, nameError, formData, isAreaSplit, categoryProfile, entityMeta, entities, activeEntityIdx]);

  const multiBuildingIncomplete = useMemo(() => {
    if (step < 2 || step > 4 || entities.length <= 1 || categoryProfile.civilTwoStepWizard) return false;
    const all = commitActiveEntityToList(entities, activeEntityIdx);
    return all.some(ent => entityFailsStep(ent, step));
  }, [step, entities, activeEntityIdx, formData, entityMeta, categoryProfile]);

  const reviewEntities = useMemo(() => {
    if (step !== 5) return [];
    return commitActiveEntityToList(entities, activeEntityIdx);
  }, [step, entities, activeEntityIdx, formData, entityMeta, projectCategory, isAreaSplit, classificationOverride, occupancyRegion]);


  const isMultiBuildingReview = reviewEntities.length > 1;
  const isCivilTwoStepWizard = categoryProfile.civilTwoStepWizard;
  const isCivilReview = isCivilCategory(projectCategory) && !isCivilTwoStepWizard;
  const isPerEntityReview = !isCivilTwoStepWizard && isCivilReview;

  const step5MissingRequired = useMemo(() => {
    if (step !== 5) return false;
    if (isCivilTwoStepWizard) return false;
    if (reviewEntities.length > 1) {
      return reviewEntities.some(ent =>
        (isFieldVisible(categoryProfile, 'occupancy') && (ent.occupancyTypes?.length || 0) === 0) ||
        (isFieldVisible(categoryProfile, 'constructionType') && !ent.constructionType)
      );
    }
    if (isFieldVisible(categoryProfile, 'occupancy') && (formData.types?.length || 0) === 0) return true;
    if (isFieldVisible(categoryProfile, 'constructionType') && !formData.constructionType) return true;
    if (isFieldVisible(categoryProfile, 'storeysAboveGrade')) {
      if (!(formData.storeysAboveGrade ?? '').toString().trim()) return true;
      if (!(formData.adjacentStreets ?? '').toString().trim()) return true;
      const hasFootprint = isAreaSplit
        ? ((formData.buildingFootprintAreaExisting ?? '').toString().trim() || (formData.buildingFootprintAreaProposed ?? '').toString().trim())
        : (formData.buildingFootprintArea ?? '').toString().trim();
      if (!hasFootprint) return true;
    }
    return false;
  }, [step, categoryProfile, formData, isAreaSplit, reviewEntities, isCivilTwoStepWizard]);

  const categoryLabelForSummary = PROJECT_CATEGORY_OPTIONS.find((o) => o.value === projectCategory)?.label || projectCategory;

  const renderEntityReviewCard = (ent, idx) => {
    if (isCivilCategory(projectCategory)) {
      const typeTitles = formatInfraTypesLabel(ent.infraTypes || [], occupancyRegion) || ent.infraType || '—';
      const primaryTitle = ent.primaryInfraType
        ? (infraByKey(ent.primaryInfraType, occupancyRegion)?.title || ent.primaryInfraType)
        : '—';
      const materialLabel = MATERIAL_CONTEXT_OPTIONS.find((o) => o.value === ent.materialContext)?.label || ent.materialContext || '—';
      const floodLabel = YES_NO_UNKNOWN_OPTIONS.find((o) => o.value === ent.environmentalFloodplain)?.label || ent.environmentalFloodplain || '—';
      const swmLabel = YES_NO_UNKNOWN_OPTIONS.find((o) => o.value === ent.environmentalSwm)?.label || ent.environmentalSwm || '—';
      const metrics = getInfraMetricsProfile(ent);
      return (
        <SummarySection key={ent.id || `ent-${idx}`}>
          <SumSectionLabel>{ent.name || `Element ${idx + 1}`}</SumSectionLabel>
          <ParamRows>
            <ParamRow>
              <ParamRowLeft>
                <ParamLabel>Element Types<RequiredMark>*</RequiredMark></ParamLabel>
                <ParamValue $missing={!typeTitles}>{typeTitles}</ParamValue>
              </ParamRowLeft>
            </ParamRow>
            {(ent.infraTypes?.length || 0) > 1 && (
              <ParamRow>
                <ParamRowLeft>
                  <ParamLabel>Primary Element Type</ParamLabel>
                  <ParamValue>{primaryTitle}</ParamValue>
                </ParamRowLeft>
              </ParamRow>
            )}
            {ent.scope && (
              <ParamRow>
                <ParamRowLeft>
                  <ParamLabel>Scope</ParamLabel>
                  <ParamValue>{ent.scope}</ParamValue>
                </ParamRowLeft>
              </ParamRow>
            )}
            {ent.designStandard && (
              <ParamRow>
                <ParamRowLeft>
                  <ParamLabel>Design Standard</ParamLabel>
                  <ParamValue>{getDesignStandardLabel(ent.designStandard, occupancyRegion)}</ParamValue>
                </ParamRowLeft>
              </ParamRow>
            )}
            {metrics.showLinearExtent && ent.linearExtent && (
              <ParamRow>
                <ParamRowLeft>
                  <ParamLabel>Linear Extent</ParamLabel>
                  <ParamValue>{ent.linearExtent} {metrics.family === 'linear' ? longLinearLabel : linearLabel}</ParamValue>
                </ParamRowLeft>
              </ParamRow>
            )}
            {metrics.showArea && ent.area && (
              <ParamRow>
                <ParamRowLeft>
                  <ParamLabel>Area</ParamLabel>
                  <ParamValue>{ent.area} {areaLabel}</ParamValue>
                </ParamRowLeft>
              </ParamRow>
            )}
            <ParamRow>
              <ParamRowLeft>
                <ParamLabel>Material Context</ParamLabel>
                <ParamValue $missing={!ent.materialContext}>{materialLabel}</ParamValue>
              </ParamRowLeft>
            </ParamRow>
            <ParamRow>
              <ParamRowLeft>
                <ParamLabel>Floodplain / Environmental</ParamLabel>
                <ParamValue>{floodLabel} · SWM: {swmLabel}</ParamValue>
              </ParamRowLeft>
            </ParamRow>
          </ParamRows>
        </SummarySection>
      );
    }
    const occTitles = (ent.occupancyTypes || []).map((k) => occupancyByKey.get(k)?.title || k).join(', ');
    const majorTitle = ent.majorOccupancy
      ? (occupancyByKey.get(ent.majorOccupancy)?.title || ent.majorOccupancy)
      : '—';
    const ctTitle = ent.constructionType
      ? (constructionByKey.get(ent.constructionType)?.title || ent.constructionType)
      : '—';
    const sprinklerLabel = SPRINKLER_OPTIONS.find((o) => o.value === ent.sprinklerSystem)?.label || ent.sprinklerSystem || '—';
    return (
      <SummarySection key={ent.id || `ent-${idx}`}>
        <SumSectionLabel>{ent.name || `Building ${idx + 1}`}</SumSectionLabel>
        <ParamRows>
          <ParamRow>
            <ParamRowLeft>
              <ParamLabel>Occupancy Types<RequiredMark>*</RequiredMark></ParamLabel>
              <ParamValue $missing={!occTitles}>{occTitles || '—'}</ParamValue>
            </ParamRowLeft>
          </ParamRow>
          <ParamRow>
            <ParamRowLeft>
              <ParamLabel>Major Occupancy</ParamLabel>
              <ParamValue>{majorTitle}</ParamValue>
            </ParamRowLeft>
          </ParamRow>
          <ParamRow>
            <ParamRowLeft>
              <ParamLabel>Building Classification</ParamLabel>
              <ParamValue>{ent.buildingClassification || '—'}</ParamValue>
            </ParamRowLeft>
          </ParamRow>
          <ParamRow>
            <ParamRowLeft>
              <ParamLabel>Storeys Above Grade</ParamLabel>
              <ParamValue>{ent.storeysAboveGrade ?? '—'}</ParamValue>
            </ParamRowLeft>
          </ParamRow>
          <ParamRow>
            <ParamRowLeft>
              <ParamLabel>Adjacent Streets</ParamLabel>
              <ParamValue>{ent.adjacentStreets ?? '—'}</ParamValue>
            </ParamRowLeft>
          </ParamRow>
          <ParamRow>
            <ParamRowLeft>
              <ParamLabel>Building Footprint</ParamLabel>
              <ParamValue>
                {ent.buildingFootprintArea
                  ? `${formatWithCommas(ent.buildingFootprintArea)} ${areaLabel}`
                  : '—'}
              </ParamValue>
            </ParamRowLeft>
          </ParamRow>
          <ParamRow>
            <ParamRowLeft>
              <ParamLabel>Construction Type</ParamLabel>
              <ParamValue $missing={!ent.constructionType}>{ctTitle}</ParamValue>
            </ParamRowLeft>
          </ParamRow>
          <ParamRow>
            <ParamRowLeft>
              <ParamLabel>Sprinkler System</ParamLabel>
              <ParamValue>{sprinklerLabel}</ParamValue>
            </ParamRowLeft>
          </ParamRow>
          {ent.disciplines?.length > 0 && (
            <ParamRow>
              <ParamRowLeft>
                <ParamLabel>Disciplines in Scope</ParamLabel>
                <ParamValue>{formatEntityDisciplinesLabel(ent.disciplines)}</ParamValue>
              </ParamRowLeft>
            </ParamRow>
          )}
        </ParamRows>
      </SummarySection>
    );
  };

  // ── Summary display values ──
  const selectedTypeTitlesForSummary = (formData.types || []).map((k) => occupancyByKey.get(k)?.title || k).filter(Boolean);
  const majorOccupancyTitleForSummary = formData.majorOccupancy ? occupancyByKey.get(formData.majorOccupancy)?.title || formData.majorOccupancy : '';
  const constructionTypeTitleForSummary = formData.constructionType ? constructionByKey.get(formData.constructionType)?.title || formData.constructionType : '—';
  const projectTypeLabelForSummary = PROJECT_TYPE_OPTIONS.find((o) => o.value === formData.projectType)?.label || '—';
  const sprinklerLabelForSummary = SPRINKLER_OPTIONS.find((o) => o.value === formData.sprinklerSystem)?.label || '—';

  const meta = getStepMeta(step, categoryProfile);
  if (!isOpen) return null;

  // ── Reusable pencil row renderer ──
  const renderPencilRow = (label, field, currentVal, displayContent, editContent, required = false) => {
    const isEditing = editingField === field;
    return (
      <>
        {!isEditing && (
          <ParamRow>
            <ParamRowLeft>
              <ParamLabel>{label}{required && <RequiredMark>*</RequiredMark>}</ParamLabel>
              <ParamValueRow>
                {displayContent}
                <PencilBtn type="button" onClick={() => startEdit(field, currentVal)} title="Edit">
                  <PencilIcon />
                </PencilBtn>
              </ParamValueRow>
            </ParamRowLeft>
          </ParamRow>
        )}
        {isEditing && (
          <ParamRow $editing>
            <ParamLabel>{label}{required && <RequiredMark>*</RequiredMark>}</ParamLabel>
            {editContent}
          </ParamRow>
        )}
      </>
    );
  };

  return (
    <DialogOverlay onKeyDown={handleKeyDown}>
      <DialogContent ref={wizardRef} className="project-wizard">

        {/* Header */}
        <WizardHeader>
          <HeaderLeft>
            <WizardIcon>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            </WizardIcon>
            <TitleBlock>
              <WizardTitle>{t('project.createNewProject', 'Create a New Project')}</WizardTitle>
              <WizardSubtitle>{meta.subtitle}</WizardSubtitle>
            </TitleBlock>
          </HeaderLeft>
          <CloseButton onClick={onClose} aria-label="Close">&#x2715;</CloseButton>
        </WizardHeader>

        {/* Progress */}
        <ProgressWrap>
          <ProgressHeader>
            <ProgressLabel>{meta.label}</ProgressLabel>
            <ProgressPct>{meta.pct}%</ProgressPct>
          </ProgressHeader>
          <ProgressTrack><ProgressFill $pct={meta.pct} /></ProgressTrack>
        </ProgressWrap>

        {/* Body */}
        <WizardBody ref={formRef} $step={step}>

          {/* ── STEP 1 ── */}
          {step === 1 && (
            <>
              <FormGroup>
                <FieldLabel>{t('project.projectName', 'Project Name')}<RequiredMark>*</RequiredMark></FieldLabel>
                <InputField type="text" name="name" value={formData.name} onChange={handleNameChange}
                  placeholder={t('project.enterProjectName', 'e.g. Riverdale Office Renovation')} $error={!!nameError} />
                {nameError && <FieldError>{nameError}</FieldError>}
              </FormGroup>

              <FormGroup>
                <FieldLabel>{t('project.projectAddress', 'Project Address')}<RequiredMark>*</RequiredMark></FieldLabel>
                <AddressAutocomplete value={formData.address}
                  onChange={(address) => setFormData((prev) => ({ ...prev, address, addressSelected: false }))}
                  onAddressSelect={handleAddressSelect}
                  placeholder={t('project.enterProjectAddress', 'Start typing to search address…')} />
                <FieldHint>Address determines jurisdiction — sets occupancy types, construction types, and unit system (metric / imperial).</FieldHint>
                <InfoNote><strong>{occupancyRegion === 'CA' ? 'Canada (NBC/OBC/BCBC/CCQ)' : 'IBC (US / default)'}</strong></InfoNote>
                {formData.location && formData.location.lat !== 43.6532 && (
                  <div style={{ marginTop: '0.875rem', padding: '0.75rem', background: T.bgInput, borderRadius: T.radiusInput }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 600, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: '0.35px', marginBottom: '0.25rem' }}>Selected Location</div>
                    <div style={{ fontSize: '0.85rem', color: T.textPrimary }}>{formData.address}</div>
                    <div style={{ fontSize: '0.75rem', color: T.textTertiary, marginTop: '2px' }}>
                      Coordinates: {formData.location.lat.toFixed(6)}, {formData.location.lng.toFixed(6)}
                    </div>
                  </div>
                )}
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <FieldLabel>{t('project.projectType', 'Project Type')}<RequiredMark>*</RequiredMark></FieldLabel>
                  <SelectField name="projectType" value={formData.projectType} onChange={handleInputChange}>
                    <option value="">Select type</option>
                    {PROJECT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </SelectField>
                </FormGroup>
                <FormGroup>
                  <FieldLabel>{t('project.projectPhase', 'Project Phase')}<OptionalTag>(optional)</OptionalTag></FieldLabel>
                  <SelectField name="projectPhase" value={formData.projectPhase} onChange={handleInputChange}>
                    <option value="">Select phase</option>
                    {PROJECT_PHASE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </SelectField>
                </FormGroup>
              </FormRow>

              <FormGroup>
                <FieldLabel>Project Category</FieldLabel>
                <SelectField value={projectCategory} onChange={(e) => handleCategoryChange(e.target.value)}>
                  {PROJECT_CATEGORY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </SelectField>
                <FieldHint>{categoryProfile.description}</FieldHint>
              </FormGroup>

              {categoryProfile.civilTwoStepWizard && (
                <>
                  <SectionDivider />
                  <FormGroup>
                    <FieldLabel>Civil Verticals<RequiredMark>*</RequiredMark><OptionalTag>(select all in scope)</OptionalTag></FieldLabel>
                    <FieldHint>{CIVIL_VERTICALS_SELECTION_HINT}</FieldHint>
                    <div className="civil-vertical-grid">
                      {CIVIL_VERTICAL_OPTIONS.map((opt) => {
                        const selected = (entityMeta?.civilVerticals || []).includes(opt.value);
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className={`civil-vertical-chip${selected ? ' selected' : ''}`}
                            onClick={() => toggleCivilVertical(opt.value)}
                          >
                            <span className="civil-vertical-chip-title">{opt.label}</span>
                            <span className="civil-vertical-chip-desc">{opt.description}</span>
                          </button>
                        );
                      })}
                    </div>
                  </FormGroup>
                  <FormGroup>
                    <FieldLabel>Scope of Work / Design Brief<OptionalTag>(optional)</OptionalTag></FieldLabel>
                    <textarea
                      rows={4}
                      value={entityMeta?.scopeOfWork || ''}
                      onChange={(e) => setEntityMeta((prev) => ({ ...prev, scopeOfWork: e.target.value }))}
                      placeholder="Paste scope notes, RFP excerpt, or design brief summary…"
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        fontSize: '0.875rem',
                        fontFamily: T.font,
                        border: `1px solid ${T.borderMedium}`,
                        borderRadius: T.radiusInput,
                        resize: 'vertical',
                        minHeight: '96px',
                        boxSizing: 'border-box',
                      }}
                    />
                    <FieldHint>
                      Optional text summary for now. After the project is created, link drawings, specs, and full design brief documents from the project library.
                    </FieldHint>
                  </FormGroup>
                </>
              )}
            </>
          )}

          {/* ── STEP 2: Occupancy / entity profile ── */}
          {step === 2 && !categoryProfile.civilTwoStepWizard && (
            <>
              {renderEntityTabs()}
              {renderEntityNameFields()}
              {isFieldVisible(categoryProfile, 'occupancy') && (
              <>
              <FormGroup>
                <FieldLabel>Occupancy Types<RequiredMark>*</RequiredMark><OptionalTag>(select all that apply)</OptionalTag></FieldLabel>
                <OccPopupWrap ref={occStep2Ref}>
                  <OccDisplay onClick={() => { setSearchQuery(''); setOccStep2Open(v => !v); }}>
                    {formData.types.length === 0
                      ? <OccPlaceholder>Click to select occupancy types…</OccPlaceholder>
                      : formData.types.map(key => {
                          const occ = occupancyByKey.get(key);
                          return (
                            <OccTag key={key}>
                              {occ?.title || key}
                              <span style={{ opacity: 0.55, fontSize: '1.1rem', lineHeight: 1, cursor: 'pointer', fontWeight: 400 }}
                                onClick={e => { e.stopPropagation(); handleTypeSelect(key, occ?.code || ''); }}>×</span>
                            </OccTag>
                          );
                        })
                    }
                  </OccDisplay>
                 <OccPopupBox $open={occStep2Open}>
                    <OccPopupHeader>
                      <OccPopupTitle>Select Occupancy Types</OccPopupTitle>
                      <OccPopupSubtitle>Showing: {occupancyRegion === 'CA' ? 'Canada (Federal/Provincial)' : 'United States (IBC)'} · Select all that apply</OccPopupSubtitle>
                    </OccPopupHeader>
                    <OccSearchWrap>
                      <OccSearchInput type="text" placeholder="Search occupancy types…"
                        value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                    </OccSearchWrap>
                    <OccList>
                      {filteredOccupancies.map(([key, title, desc, code]) => {
                        const isSelected = formData.types.includes(key);
                        return (
                          <OccOption key={key} $selected={isSelected} onClick={() => handleTypeSelect(key, code)}>
                            <OccCheckbox $checked={isSelected}>{isSelected ? '✓' : ''}</OccCheckbox>
                            <div>
                              <OccOptionTitle>{title} <OccOptionCode>({code.toUpperCase()})</OccOptionCode></OccOptionTitle>
                              <OccOptionDesc>{desc}</OccOptionDesc>
                            </div>
                          </OccOption>
                        );
                      })}
                    </OccList>
                    <OccPopupFooter>
                      <OccCount>{formData.types.length} selected</OccCount>
                      <OccDoneBtn onClick={() => setOccStep2Open(false)}>Done</OccDoneBtn>
                    </OccPopupFooter>
                  </OccPopupBox>
                </OccPopupWrap>
                <FieldHint>Showing: <strong>{occupancyRegion === 'CA' ? 'Canada (Federal/Provincial)' : 'United States (IBC)'}</strong>. Click to open selector.</FieldHint>
              </FormGroup>

              {formData.types.length > 1 && (
                <>
                  <SectionDivider />
                  <FormGroup>
                    <FieldLabel>Major Occupancy<RequiredMark>*</RequiredMark></FieldLabel>
                    <SelectField name="majorOccupancy" value={formData.majorOccupancy} onChange={handleInputChange}>
                      <option value="">Select major occupancy</option>
                      {formData.types.map((key) => <option key={key} value={key}>{occupancyByKey.get(key)?.title || key}</option>)}
                    </SelectField>
                    <FieldHint>Select the dominant occupancy type for code classification.</FieldHint>
                  </FormGroup>
                </>
              )}

              <SectionDivider />

              {/* Building Classification on Step 2 — matching HTML */}
              <FormGroup>
                <FieldLabel>Building Classification</FieldLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {!classStep2Editing ? (
                    <ReadOnlyDisplay style={{ flex: 1 }}>
                      <span>{effectiveClassification || '—'}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {classificationOverride
                          ? <ManualBadge onClick={clearClassOverride} title="Click to restore auto-compute">Manually set ✕</ManualBadge>
                          : <AutoBadge>Auto-computed</AutoBadge>}
                      </div>
                    </ReadOnlyDisplay>
                  ) : (
                    <SelectField value={classStep2Draft} style={{ flex: 1 }}
                      onChange={e => setClassStep2Draft(e.target.value)}>
                      {getClassificationOptions(occupancyRegion).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </SelectField>
                  )}
                  {!classStep2Editing
                    ? <PencilBtn type="button" onClick={startClassStep2Edit} title="Edit"><PencilIcon /></PencilBtn>
                    : <>
                        <ParamSaveBtn onClick={saveClassStep2}>Save</ParamSaveBtn>
                        <ParamCancelBtn onClick={cancelClassStep2}>Cancel</ParamCancelBtn>
                      </>
                  }
                </div>
                <FieldHint>Computed from storeys, footprint area, and occupancy. Override if CODiii's suggestion doesn't match your professional judgment.</FieldHint>
              </FormGroup>
              </>
              )}
              {isFieldVisible(categoryProfile, 'infraTypes') && (
                <>
                  <InfraTypeSelector
                    occupancyRegion={occupancyRegion}
                    selectedKeys={entityMeta?.infraTypes || []}
                    primaryKey={entityMeta?.primaryInfraType || ''}
                    onChange={handleInfraTypeChange}
                    required
                  />
                  <FormGroup>
                    <FieldLabel>Scope</FieldLabel>
                    <textarea
                      rows={3}
                      value={entityMeta?.scope || ''}
                      onChange={(e) => setEntityMeta((prev) => ({ ...prev, scope: e.target.value }))}
                      placeholder="Brief scope description for this element"
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        fontSize: '0.875rem',
                        fontFamily: T.font,
                        border: `1px solid ${T.borderMedium}`,
                        borderRadius: T.radiusInput,
                        resize: 'vertical',
                        minHeight: '72px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </FormGroup>
                  {isFieldVisible(categoryProfile, 'designStandard') && (
                    <FormGroup>
                      <FieldLabel>Design Standard</FieldLabel>
                      <SelectField
                        value={entityMeta?.designStandard || ''}
                        onChange={(e) => setEntityMeta((prev) => ({ ...prev, designStandard: e.target.value }))}
                      >
                        <option value="">Select design standard (optional)</option>
                        {getDesignStandardOptions(occupancyRegion).map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </SelectField>
                    </FormGroup>
                  )}
                </>
              )}
            </>
          )}

          {/* ── STEP 3: Building Parameters ── */}
          {step === 3 && !categoryProfile.civilTwoStepWizard && (
            <>
              {renderEntityTabs()}
              {(isFieldVisible(categoryProfile, 'storeysAboveGrade') || isFieldVisible(categoryProfile, 'buildingFootprintArea')) && (
              <>
              <FormRow>
                <FormGroup>
                  <FieldLabel>Storeys Above Grade<RequiredMark>*</RequiredMark></FieldLabel>
                  <InputField type="text" name="storeysAboveGrade" value={formData.storeysAboveGrade}
                    onChange={(e) => { const r = e.target.value; if (!/^\d*$/.test(r)) return; setFormData(p => ({ ...p, storeysAboveGrade: r })); }}
                    placeholder="e.g. 3" />
                </FormGroup>
                <FormGroup>
                  <FieldLabel>Storeys Below Grade</FieldLabel>
                  <InputField type="text" name="storeysBelowGrade" value={formData.storeysBelowGrade}
                    onChange={(e) => { const r = e.target.value; if (!/^\d*$/.test(r)) return; setFormData(p => ({ ...p, storeysBelowGrade: r })); }}
                    placeholder="e.g. 1" />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <FieldLabel>Adjacent Streets<RequiredMark>*</RequiredMark></FieldLabel>
                  <InputField type="text" name="adjacentStreets" value={formData.adjacentStreets}
                    onChange={(e) => { const r = e.target.value; if (!/^\d*$/.test(r)) return; setFormData(p => ({ ...p, adjacentStreets: r })); }}
                    placeholder="e.g. 2" />
                  <FieldHint>Streets the building faces</FieldHint>
                </FormGroup>
                <FormGroup>
                  <FieldLabel>Building Height ({linearLabel})</FieldLabel>
                  <InputField type="text" name="buildingHeight" value={formData.buildingHeight}
                    onChange={(e) => { const r = e.target.value; if (!/^\d*\.?\d*$/.test(r)) return; setFormData(p => ({ ...p, buildingHeight: r })); }}
                    placeholder="e.g. 12.5" />
                </FormGroup>
              </FormRow>

              <SectionDivider />

              <FormGroup>
                <FieldLabel>Building Footprint Area ({areaLabel})<RequiredMark>*</RequiredMark></FieldLabel>
                {isAreaSplit ? (
                  <>
                    <AreaSplitWrapper>
                      <div>
                        <SubLabel>Existing</SubLabel>
                        <InputField type="text" value={formData.buildingFootprintAreaExisting}
                          onChange={(e) => { const r = e.target.value; if (!/^\d*\.?\d*$/.test(r)) return; setFormData(p => ({ ...p, buildingFootprintAreaExisting: r })); }}
                          placeholder="0" />
                      </div>
                      <div>
                        <SubLabel>Proposed</SubLabel>
                        <InputField type="text" value={formData.buildingFootprintAreaProposed}
                          onChange={(e) => { const r = e.target.value; if (!/^\d*\.?\d*$/.test(r)) return; setFormData(p => ({ ...p, buildingFootprintAreaProposed: r })); }}
                          placeholder="0" />
                      </div>
                    </AreaSplitWrapper>
                    {effectiveFootprintArea && <AreaTotal>Total: <strong>{formatWithCommas(effectiveFootprintArea)} {areaLabel}</strong></AreaTotal>}
                  </>
                ) : (
                  <InputField type="text" value={formatWithCommas(formData.buildingFootprintArea)}
                    onChange={(e) => { const r = removeCommas(e.target.value); if (!/^\d*\.?\d*$/.test(r)) return; setFormData(p => ({ ...p, buildingFootprintArea: r })); }}
                    placeholder="e.g. 450" />
                )}
              </FormGroup>

              <FormGroup>
                <FieldLabel>Gross Floor Area ({areaLabel})</FieldLabel>
                {isAreaSplit ? (
                  <>
                    <AreaSplitWrapper>
                      <div>
                        <SubLabel>Existing</SubLabel>
                        <InputField type="text" value={formData.grossFloorAreaExisting}
                          onChange={(e) => { const r = e.target.value; if (!/^\d*\.?\d*$/.test(r)) return; setFormData(p => ({ ...p, grossFloorAreaExisting: r })); }}
                          placeholder="0" />
                      </div>
                      <div>
                        <SubLabel>Proposed</SubLabel>
                        <InputField type="text" value={formData.grossFloorAreaProposed}
                          onChange={(e) => { const r = e.target.value; if (!/^\d*\.?\d*$/.test(r)) return; setFormData(p => ({ ...p, grossFloorAreaProposed: r })); }}
                          placeholder="0" />
                      </div>
                    </AreaSplitWrapper>
                    {(formData.grossFloorAreaExisting || formData.grossFloorAreaProposed) && (
                      <AreaTotal>Total: <strong>{formatWithCommas(String(Math.round(((parseFloat(formData.grossFloorAreaExisting||'')||0)+(parseFloat(formData.grossFloorAreaProposed||'')||0))*100)/100))} {areaLabel}</strong></AreaTotal>
                    )}
                  </>
                ) : (
                  <InputField type="text" value={formatWithCommas(formData.grossFloorArea)}
                    onChange={(e) => { const r = removeCommas(e.target.value); if (!/^\d*\.?\d*$/.test(r)) return; setFormData(p => ({ ...p, grossFloorArea: r })); }}
                    placeholder="e.g. 1350" />
                )}
              </FormGroup>

              <SectionDivider />

              <FormRow3>
                <FormGroup>
                  <FieldLabel>Site Area ({areaLabel})</FieldLabel>
                  <InputField type="text" value={formatWithCommas(formData.siteArea)}
                    onChange={(e) => { const r = removeCommas(e.target.value); if (!/^\d*\.?\d*$/.test(r)) return; setFormData(p => ({ ...p, siteArea: r })); }}
                    placeholder="e.g. 2500" />
                </FormGroup>
                <FormGroup>
                  <FieldLabel>Average Grade ({linearLabel})</FieldLabel>
                  <InputField type="text" name="averageGrade" value={formData.averageGrade}
                    onChange={(e) => { const r = e.target.value; if (!/^\d*\.?\d*$/.test(r)) return; setFormData(p => ({ ...p, averageGrade: r })); }}
                    placeholder="e.g. 2.5" />
                </FormGroup>
                <FormGroup>
                  <FieldLabel>High Building</FieldLabel>
                  <SelectField name="highBuilding" value={formData.highBuilding} onChange={handleInputChange}>
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </SelectField>
                </FormGroup>
              </FormRow3>

              <SectionDivider />

              {/* Building Classification on Step 3 — same override UI as Step 2 */}
              <FormGroup>
                <FieldLabel>Building Classification</FieldLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {classificationOverride ? (
                    <>
                      <SelectField value={classificationOverride} style={{ flex: 1 }}
                        onChange={e => setClassificationOverride(e.target.value || null)}>
                        {getClassificationOptions(occupancyRegion).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </SelectField>
                      <WizardBtn $ghost type="button"
                        style={{ whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '0.4rem 0.75rem' }}
                        onClick={() => setClassificationOverride(null)}>Clear</WizardBtn>
                    </>
                  ) : (
                    <ReadOnlyDisplay style={{ flex: 1 }}>
                      <span>{occupancyRegion === 'CA' ? `${buildingClassification} (NBC/OBC)` : 'IBC (US)'}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <AutoBadge>Auto-computed</AutoBadge>
                        <WizardBtn $ghost type="button"
                          style={{ fontSize: '0.75rem', padding: '0.3rem 0.625rem' }}
                          onClick={() => setClassificationOverride(buildingClassification)}>Override</WizardBtn>
                      </div>
                    </ReadOnlyDisplay>
                  )}
                </div>
                <FieldHint>
                  {classificationOverride
                    ? 'Manual override active — auto-computation is bypassed.'
                    : 'Computed from storeys, footprint area, and occupancy. Updates automatically.'}
                </FieldHint>
              </FormGroup>
              </>
              )}
              {(isFieldVisible(categoryProfile, 'linearExtent') || isFieldVisible(categoryProfile, 'area')) && (
                <>
                  {renderInfraMetricsFields(categoryProfile.wizardMetricsRequired === false)}
                </>
              )}
            </>
          )}

          {/* ── STEP 4: Compliance Context ── */}
          {step === 4 && !categoryProfile.civilTwoStepWizard && (
            <>
              {renderEntityTabs()}
              {isFieldVisible(categoryProfile, 'constructionType') && (
              <FormGroup>
                <FieldLabel>Construction Type<RequiredMark>*</RequiredMark></FieldLabel>
                <SelectField name="constructionType" value={formData.constructionType} onChange={handleInputChange}>
                  <option value="">Select construction type</option>
                  {CONSTRUCTION_LIST.map(([key, title]) => <option key={key} value={key}>{title}</option>)}
                </SelectField>
                {!formData.constructionType && (
                  <WarningNote>Construction type not set — building code extraction will run unfiltered. Set this here or in Project Settings before linking a building code document.</WarningNote>
                )}
                {formData.constructionType && constructionByKey.get(formData.constructionType) && (
                  <InfoNote>{constructionByKey.get(formData.constructionType).desc}</InfoNote>
                )}
              </FormGroup>
              )}

              {isFieldVisible(categoryProfile, 'compliance') && (
              <>
              <SectionDivider />

              <FormGroup>
                <FieldLabel>Sprinkler System</FieldLabel>
                <SelectField name="sprinklerSystem" value={formData.sprinklerSystem || 'unknown'} onChange={handleInputChange}>
                  {SPRINKLER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </SelectField>
                <FieldHint>When unknown, both sprinklered and non-sprinklered requirement branches are extracted.</FieldHint>
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <FieldLabel>Fire Alarm</FieldLabel>
                  <ToggleGroup
                    value={formData.fireAlarm || 'unknown'}
                    options={YES_NO_UNKNOWN_OPTIONS}
                    onChange={v => setFormData(p => ({ ...p, fireAlarm: v }))}
                  />
                </FormGroup>
                <FormGroup>
                  <FieldLabel>Standpipe</FieldLabel>
                  <ToggleGroup
                    value={formData.standpipe || 'unknown'}
                    options={YES_NO_UNKNOWN_OPTIONS}
                    onChange={v => setFormData(p => ({ ...p, standpipe: v }))}
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <FieldLabel>Hazardous Substances</FieldLabel>
                <ToggleGroup
                  value={formData.hazardousSubstances || 'no'}
                  options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
                  onChange={v => setFormData(p => ({ ...p, hazardousSubstances: v }))}
                />
                <FieldHint>Group H hazardous material provisions are included when "Yes" is declared.</FieldHint>
              </FormGroup>
              {isFieldVisible(categoryProfile, 'disciplines') && (
                <FormGroup>
                  <FieldLabel>Disciplines in Scope</FieldLabel>
                  <FieldHint>
                    Optional — limit scope to selected disciplines for this building.
                    {entityMeta?.disciplines?.length
                      ? ` Selected: ${formatEntityDisciplinesLabel(entityMeta.disciplines)}`
                      : ' None selected = all disciplines.'}
                  </FieldHint>
                  <div className="entity-discipline-checks" style={{ marginTop: '0.5rem' }}>
                    {ENTITY_DISCIPLINE_OPTIONS.map((opt) => (
                      <label key={opt.value} className="entity-discipline-check" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', marginRight: '0.75rem', fontSize: '0.82rem' }}>
                        <input
                          type="checkbox"
                          checked={(entityMeta?.disciplines || []).includes(opt.value)}
                          onChange={() => toggleEntityDiscipline(opt.value)}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </FormGroup>
              )}
              <InfoNote>Declare fire protection fields based on your project program and code review. If unsure, leave as Unknown — you can update these in Project Settings at any time.</InfoNote>
              </>
              )}

              {isFieldVisible(categoryProfile, 'materialContext') && (
                <>
                  <SectionDivider />
                  <FormGroup>
                    <FieldLabel>
                      Material Context
                      {categoryProfile.wizardComplianceRequired !== false && <RequiredMark>*</RequiredMark>}
                      {categoryProfile.wizardComplianceRequired === false && <OptionalTag>(optional)</OptionalTag>}
                    </FieldLabel>
                    <SelectField
                      value={entityMeta?.materialContext || ''}
                      onChange={(e) => setEntityMeta((prev) => ({ ...prev, materialContext: e.target.value }))}
                    >
                      <option value="">Select material context</option>
                      {MATERIAL_CONTEXT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </SelectField>
                  </FormGroup>
                  {isFieldVisible(categoryProfile, 'environmental') && (
                    <FormRow>
                      <FormGroup>
                        <FieldLabel>Floodplain / Environmental Constraints</FieldLabel>
                        <ToggleGroup
                          value={entityMeta?.environmentalFloodplain || 'unknown'}
                          options={YES_NO_UNKNOWN_OPTIONS}
                          onChange={(v) => setEntityMeta((prev) => ({ ...prev, environmentalFloodplain: v }))}
                        />
                      </FormGroup>
                      <FormGroup>
                        <FieldLabel>Stormwater Management Criteria</FieldLabel>
                        <ToggleGroup
                          value={entityMeta?.environmentalSwm || 'unknown'}
                          options={YES_NO_UNKNOWN_OPTIONS}
                          onChange={(v) => setEntityMeta((prev) => ({ ...prev, environmentalSwm: v }))}
                        />
                      </FormGroup>
                    </FormRow>
                  )}
                  <InfoNote>Site infrastructure compliance focuses on materials and environmental context — not building life-safety systems.</InfoNote>
                </>
              )}

              <SectionDivider />

              <FormGroup>
                <FieldLabel>Rough Construction Budget ($)</FieldLabel>
                <InputField type="text" name="roughConstructionBudget"
                  value={formatWithCommas(formData.roughConstructionBudget)}
                  onChange={(e) => { const r = removeCommas(e.target.value); if (!/^\d*$/.test(r)) return; setFormData(p => ({ ...p, roughConstructionBudget: r })); }}
                  placeholder="e.g. 2,500,000" />
              </FormGroup>
            </>
          )}

          {/* ── STEP 5: Summary with inline editing ── */}
          {step === 5 && (
            <>
              {/* Project Basics */}
              <SummarySection>
                <SumSectionLabel>Project Basics</SumSectionLabel>
                <ParamRows>

                  {/* Project Name */}
                  {renderPencilRow('Project Name', 'name', formData.name,
                    <ParamValue $missing={!formData.name}>{formData.name || '—'}</ParamValue>,
                    <div style={{ width: '100%' }}>
                      <ParamEditControls>
                        <ParamEditInput type="text" value={editDraft['name'] ?? formData.name}
                          onChange={e => setEditDraft(p => ({ ...p, name: e.target.value }))}
                          autoFocus />
                        <ParamSaveBtn
                          disabled={!(editDraft['name'] ?? formData.name ?? '').toString().trim()}
                          onClick={() => saveEditText('name', 'name')}>Save</ParamSaveBtn>
                        <ParamCancelBtn onClick={cancelEdit}>Cancel</ParamCancelBtn>
                      </ParamEditControls>
                      {!(editDraft['name'] ?? formData.name ?? '').toString().trim() && (
                        <FieldError>Project name can't be empty.</FieldError>
                      )}
                    </div>,
                    true
                  )}

                  {/* Project Address */}
                  {renderPencilRow(
                    'Project Address', 'address', formData.address,
                    <ParamValue $missing={!formData.address} style={{ textAlign: 'right', maxWidth: '260px' }}>{formData.address || '—'}</ParamValue>,
                    <div style={{ width: '100%' }}>
                      <ParamEditControls $stacked>
                      <AddressAutocomplete
                        value={editDraft['address'] ?? formData.address}
                        onChange={address => setEditDraft(p => ({ ...p, address, addressSelected: false }))}
                        onAddressSelect={addressData => {
                          setEditDraft(p => ({
                            ...p,
                            address: addressData.address,
                            location: addressData.location,
                            countryCode: addressData.countryCode,
                            addressSelected: true,
                          }));
                        }}
                        placeholder="Start typing address…"
                      />
                      <ParamEditActions>
                        <ParamSaveBtn
                          disabled={!editDraft['addressSelected']}
                          onClick={() => {
                            setFormData(p => ({
                              ...p,
                              address: editDraft['address'],
                              location: editDraft['location'] ?? p.location,
                            }));
                            setOccupancyRegion(editDraft['countryCode'] === 'CA' ? 'CA' : 'US');
                            setEditingField(null);
                          }}>Save</ParamSaveBtn>
                        <ParamCancelBtn onClick={cancelEdit}>Cancel</ParamCancelBtn>
                      </ParamEditActions>
                    </ParamEditControls>
                      {!(editDraft['address'] ?? formData.address ?? '').toString().trim() && (
                        <FieldError>Project address can't be empty.</FieldError>
                      )}
                    </div>,
                    true
                  )}

                  {/* Project Type */}
                  {renderPencilRow(
                    'Project Type', 'projectType', formData.projectType,
                    <ParamValue $missing={!formData.projectType}>{projectTypeLabelForSummary}</ParamValue>,
                    <div style={{ width: '100%' }}>
                      <ParamEditControls>
                        <SelectField value={editDraft['projectType'] ?? formData.projectType}
                          onChange={e => setEditDraft(p => ({ ...p, projectType: e.target.value }))}
                          style={{ flex: 1 }} autoFocus>
                          <option value="">Select type</option>
                          {PROJECT_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </SelectField>
                        <ParamSaveBtn
                          disabled={!(editDraft['projectType'] ?? formData.projectType ?? '').toString().trim()}
                          onClick={() => saveEditDropdown('projectType', 'projectType')}>Save</ParamSaveBtn>
                        <ParamCancelBtn onClick={cancelEdit}>Cancel</ParamCancelBtn>
                      </ParamEditControls>
                      {!(editDraft['projectType'] ?? formData.projectType ?? '').toString().trim() && (
                        <FieldError>Project type can't be empty.</FieldError>
                      )}
                    </div>,
                    true
                  )}

                  {/* Project Phase */}
                  {renderPencilRow(
                    'Project Phase', 'projectPhase', formData.projectPhase,
                    <ParamValue $missing={!formData.projectPhase}>{PROJECT_PHASE_OPTIONS.find(o => o.value === formData.projectPhase)?.label || '—'}</ParamValue>,
                    <ParamEditControls>
                      <SelectField value={editDraft['projectPhase'] ?? formData.projectPhase}
                        onChange={e => setEditDraft(p => ({ ...p, projectPhase: e.target.value }))}
                        style={{ flex: 1 }} autoFocus>
                        <option value="">Select phase</option>
                        {PROJECT_PHASE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </SelectField>
                      <ParamSaveBtn onClick={() => saveEditDropdown('projectPhase', 'projectPhase')}>Save</ParamSaveBtn>
                      <ParamCancelBtn onClick={cancelEdit}>Cancel</ParamCancelBtn>
                    </ParamEditControls>
                  )}
                </ParamRows>
              </SummarySection>

              {isCivilTwoStepWizard ? (
                <>
                  <SummarySection>
                    <SumSectionLabel>Civil Scope</SumSectionLabel>
                    <ParamRows>
                      <ParamRow>
                        <ParamRowLeft>
                          <ParamLabel>Project Category</ParamLabel>
                          <ParamValue>{categoryLabelForSummary}</ParamValue>
                        </ParamRowLeft>
                      </ParamRow>
                      <ParamRow>
                        <ParamRowLeft>
                          <ParamLabel>Civil Verticals<RequiredMark>*</RequiredMark></ParamLabel>
                          <ParamValue $missing={!(entityMeta?.civilVerticals?.length)}>
                            {formatCivilVerticalsLabel(entityMeta?.civilVerticals || []) || '—'}
                          </ParamValue>
                        </ParamRowLeft>
                      </ParamRow>
                      <ParamRow>
                        <ParamRowLeft>
                          <ParamLabel>Scope of Work / Design Brief</ParamLabel>
                          <ParamValue $missing={!entityMeta?.scopeOfWork?.trim()} style={{ textAlign: 'right', maxWidth: '280px' }}>
                            {entityMeta?.scopeOfWork?.trim() || '—'}
                          </ParamValue>
                        </ParamRowLeft>
                      </ParamRow>
                    </ParamRows>
                  </SummarySection>
                  <InfoNote>
                    Element types, metrics, and compliance can be added per component in Project Settings after you link civil drawings and specs.
                  </InfoNote>
                </>
              ) : (
                <>
              {isMultiBuildingReview && (
                <SummarySection>
                  <SumSectionLabel>Site</SumSectionLabel>
                  <ParamRows>
                    <ParamRow>
                      <ParamRowLeft>
                        <ParamLabel>Project Category</ParamLabel>
                        <ParamValue>{categoryLabelForSummary}</ParamValue>
                      </ParamRowLeft>
                    </ParamRow>
                    <ParamRow>
                      <ParamRowLeft>
                        <ParamLabel>Buildings on Site</ParamLabel>
                        <ParamValue>{reviewEntities.length} buildings</ParamValue>
                      </ParamRowLeft>
                    </ParamRow>
                  </ParamRows>
                </SummarySection>
              )}

              {isPerEntityReview ? (
                <>
                {reviewEntities.map((ent, idx) => renderEntityReviewCard(ent, idx))}
                <SummarySection>
                  <SumSectionLabel>Project</SumSectionLabel>
                  <ParamRows>
                    {renderPencilRow(
                      'Rough Construction Budget', 'roughConstructionBudget', formData.roughConstructionBudget,
                      <ParamValue $missing={!formData.roughConstructionBudget}>
                        {formData.roughConstructionBudget ? `$${formatWithCommas(formData.roughConstructionBudget)}` : '—'}
                      </ParamValue>,
                      <ParamEditControls>
                        <ParamEditInput type="text"
                          value={editDraft['roughConstructionBudget'] ?? formatWithCommas(formData.roughConstructionBudget)}
                          onChange={e => { const r = removeCommas(e.target.value); if (!/^\d*$/.test(r)) return; setEditDraft(p => ({ ...p, roughConstructionBudget: r })); }}
                          placeholder="e.g. 2,500,000" autoFocus />
                        <ParamSaveBtn onClick={() => { setFormData(p => ({ ...p, roughConstructionBudget: editDraft['roughConstructionBudget'] ?? p.roughConstructionBudget })); setEditingField(null); }}>Save</ParamSaveBtn>
                        <ParamCancelBtn onClick={cancelEdit}>Cancel</ParamCancelBtn>
                      </ParamEditControls>
                    )}
                  </ParamRows>
                </SummarySection>
                </>
              ) : (
              <>
              {reviewEntities.length > 1 && (
                <div className="entity-tabs-bar" style={{ marginBottom: '1rem' }}>
                  {reviewEntities.map((ent, idx) => (
                    <button key={ent.id || idx} type="button"
                      className={`entity-tab${activeEntityIdx === idx ? ' active' : ''}`}
                      onClick={() => { setEditingField(null); switchEntityTab(idx); }}>
                      {ent.name || `Building ${idx + 1}`}
                    </button>
                  ))}
                </div>
              )}
              {/* Occupancy */}
              <SummarySection>
                <SumSectionLabel>Occupancy</SumSectionLabel>
                <ParamRows>

                  {/* Occupancy Types — opens right popup */}
                  <ParamRow>
                    <ParamRowLeft>
                      <ParamLabel>Occupancy Types<RequiredMark>*</RequiredMark></ParamLabel>
                      <ParamValueRow>
                        <ParamValue $missing={!selectedTypeTitlesForSummary.length}
                          style={{ textAlign: 'right', maxWidth: '260px' }}>
                          {selectedTypeTitlesForSummary.join(', ') || '—'}
                        </ParamValue>
                        <PencilBtn type="button" onClick={() => { setSearchQueryRight(''); setOccRightOpen(true); }} title="Edit">
                          <PencilIcon />
                        </PencilBtn>
                      </ParamValueRow>
                    </ParamRowLeft>
                  </ParamRow>
                  {isFieldVisible(categoryProfile, 'occupancy') && !selectedTypeTitlesForSummary.length && (
                    <FieldError style={{ padding: '0 0.75rem' }}>
                      At least one occupancy type is required. Select one before creating the project.
                    </FieldError>
                  )}

                  {/* Major Occupancy */}
                  {renderPencilRow(
                    'Major Occupancy', 'majorOccupancy', formData.majorOccupancy,
                    <ParamValue $missing={!majorOccupancyTitleForSummary}>{majorOccupancyTitleForSummary || '—'}</ParamValue>,
                    <ParamEditControls $stacked>
                      <SelectField value={editDraft['majorOccupancy'] ?? formData.majorOccupancy}
                        onChange={e => setEditDraft(p => ({ ...p, majorOccupancy: e.target.value }))}
                        autoFocus>
                        <option value="">Select major occupancy</option>
                        {formData.types.map(key => <option key={key} value={key}>{occupancyByKey.get(key)?.title || key}</option>)}
                      </SelectField>
                      <ParamEditActions>
                        <ParamSaveBtn onClick={() => saveEditDropdown('majorOccupancy', 'majorOccupancy')}>Save</ParamSaveBtn>
                        <ParamCancelBtn onClick={cancelEdit}>Cancel</ParamCancelBtn>
                      </ParamEditActions>
                    </ParamEditControls>
                  )}

                  {/* Building Classification */}
                  <ParamRow>
                    <ParamRowLeft>
                      <ParamLabel>Building Classification<RequiredMark>*</RequiredMark></ParamLabel>
                      <ParamValueRow>
                        <ParamValue>{effectiveClassification}</ParamValue>
                        {classificationOverride
                          ? <ManualBadge onClick={clearClassificationFromSummary} title="Click to restore auto-compute">Manually set ✕</ManualBadge>
                          : <AutoBadge>Auto-computed</AutoBadge>}
                        <PencilBtn type="button"
                          onClick={() => startEdit('classification', classificationOverride || buildingClassification)}
                          title="Edit"><PencilIcon /></PencilBtn>
                      </ParamValueRow>
                    </ParamRowLeft>
                  </ParamRow>
                  {editingField === 'classification' && (
                    <ParamRow $editing>
                      <ParamLabel>Building Classification</ParamLabel>
                      <ParamEditControls>
                        <SelectField value={editDraft['classification'] ?? effectiveClassification}
                          onChange={e => setEditDraft(p => ({ ...p, classification: e.target.value }))}
                          style={{ flex: 1 }} autoFocus>
                          {getClassificationOptions(occupancyRegion).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </SelectField>
                        <ParamSaveBtn onClick={saveEditClassification}>Save</ParamSaveBtn>
                        <ParamCancelBtn onClick={cancelEdit}>Cancel</ParamCancelBtn>
                      </ParamEditControls>
                    </ParamRow>
                  )}
                </ParamRows>
              </SummarySection>

              {/* Building Parameters */}
              <SummarySection>
                <SumSectionLabel>Building Parameters</SumSectionLabel>
                <ParamRows>

                  {renderPencilRow(
                    'Storeys Above Grade', 'storeysAboveGrade', formData.storeysAboveGrade,
                    <ParamValue $missing={!formData.storeysAboveGrade}>{formData.storeysAboveGrade || '—'}</ParamValue>,
                    <div style={{ width: '100%' }}>
                      <ParamEditControls>
                        <ParamEditInput type="number" min="1" value={editDraft['storeysAboveGrade'] ?? formData.storeysAboveGrade}
                          onChange={e => setEditDraft(p => ({ ...p, storeysAboveGrade: e.target.value }))} autoFocus />
                        <ParamSaveBtn
                          disabled={!(editDraft['storeysAboveGrade'] ?? formData.storeysAboveGrade ?? '').toString().trim()}
                          onClick={() => saveEditNumberRequired('storeysAboveGrade', 'storeysAboveGrade')}>Save</ParamSaveBtn>
                        <ParamCancelBtn onClick={cancelEdit}>Cancel</ParamCancelBtn>
                      </ParamEditControls>
                      {!(editDraft['storeysAboveGrade'] ?? formData.storeysAboveGrade ?? '').toString().trim() && (
                        <FieldError>Storeys above grade can't be empty.</FieldError>
                      )}
                    </div>,
                    true
                  )}
                  {!(formData.storeysAboveGrade ?? '').toString().trim() && (
                    <FieldError style={{ padding: '0 0.75rem' }}>Storeys above grade is required.</FieldError>
                  )}

                  {renderPencilRow(
                    'Storeys Below Grade', 'storeysBelowGrade', formData.storeysBelowGrade,
                    <ParamValue $missing={!formData.storeysBelowGrade}>{formData.storeysBelowGrade || '—'}</ParamValue>,
                    <ParamEditControls>
                      <ParamEditInput type="number" min="0" value={editDraft['storeysBelowGrade'] ?? formData.storeysBelowGrade}
                        onChange={e => setEditDraft(p => ({ ...p, storeysBelowGrade: e.target.value }))} autoFocus />
                      <ParamSaveBtn onClick={() => saveEditNumber('storeysBelowGrade', 'storeysBelowGrade')}>Save</ParamSaveBtn>
                      <ParamCancelBtn onClick={cancelEdit}>Cancel</ParamCancelBtn>
                    </ParamEditControls>
                  )}

                  {renderPencilRow(
                    'Adjacent Streets', 'adjacentStreets', formData.adjacentStreets,
                    <ParamValue $missing={!formData.adjacentStreets}>{formData.adjacentStreets || '—'}</ParamValue>,
                    <div style={{ width: '100%' }}>
                      <ParamEditControls>
                        <ParamEditInput type="number" min="0" max="4" value={editDraft['adjacentStreets'] ?? formData.adjacentStreets}
                          onChange={e => setEditDraft(p => ({ ...p, adjacentStreets: e.target.value }))} autoFocus />
                        <ParamSaveBtn
                          disabled={!(editDraft['adjacentStreets'] ?? formData.adjacentStreets ?? '').toString().trim()}
                          onClick={() => saveEditNumberRequired('adjacentStreets', 'adjacentStreets')}>Save</ParamSaveBtn>
                        <ParamCancelBtn onClick={cancelEdit}>Cancel</ParamCancelBtn>
                      </ParamEditControls>
                      {!(editDraft['adjacentStreets'] ?? formData.adjacentStreets ?? '').toString().trim() && (
                        <FieldError>Adjacent streets can't be empty.</FieldError>
                      )}
                    </div>,
                    true
                  )}
                  {!(formData.adjacentStreets ?? '').toString().trim() && (
                    <FieldError style={{ padding: '0 0.75rem' }}>Adjacent streets is required.</FieldError>
                  )}

                  {/* Building Footprint Area */}
                  {renderPencilRow(
                    'Building Footprint Area', 'footprint', formData.buildingFootprintArea, 
                    isAreaSplit
                      ? <ParamValue>Existing {formData.buildingFootprintAreaExisting || '0'} + Proposed {formData.buildingFootprintAreaProposed || '0'} = <strong>{effectiveFootprintArea || '0'} {areaLabel}</strong></ParamValue>
                      : <ParamValue $missing={!formData.buildingFootprintArea}>{formData.buildingFootprintArea ? `${formatWithCommas(formData.buildingFootprintArea)} ${areaLabel}` : '—'}</ParamValue>,
                    isAreaSplit ? (
                      <div style={{ width: '100%' }}>
                        <ParamEditControls $stacked>
                          <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
                            <div style={{ flex: 1 }}><SubLabel>Existing</SubLabel>
                              <ParamEditInput type="number" min="0" value={editDraft['fpExisting'] ?? formData.buildingFootprintAreaExisting}
                                onChange={e => setEditDraft(p => ({ ...p, fpExisting: e.target.value }))} autoFocus /></div>
                            <div style={{ flex: 1 }}><SubLabel>Proposed</SubLabel>
                              <ParamEditInput type="number" min="0" value={editDraft['fpProposed'] ?? formData.buildingFootprintAreaProposed}
                                onChange={e => setEditDraft(p => ({ ...p, fpProposed: e.target.value }))} /></div>
                          </div>
                          <ParamEditActions>
                            <ParamSaveBtn
                              disabled={!((editDraft['fpExisting'] ?? formData.buildingFootprintAreaExisting ?? '').toString().trim() || (editDraft['fpProposed'] ?? formData.buildingFootprintAreaProposed ?? '').toString().trim())}
                              onClick={() => { setFormData(p => ({ ...p, buildingFootprintAreaExisting: editDraft['fpExisting'] ?? p.buildingFootprintAreaExisting, buildingFootprintAreaProposed: editDraft['fpProposed'] ?? p.buildingFootprintAreaProposed })); setEditingField(null); }}>Save</ParamSaveBtn>
                            <ParamCancelBtn onClick={cancelEdit}>Cancel</ParamCancelBtn>
                          </ParamEditActions>
                        </ParamEditControls>
                        {!((editDraft['fpExisting'] ?? formData.buildingFootprintAreaExisting ?? '').toString().trim() || (editDraft['fpProposed'] ?? formData.buildingFootprintAreaProposed ?? '').toString().trim()) && (
                          <FieldError>Enter at least one of Existing or Proposed.</FieldError>
                        )}
                      </div>
                    ) : (
                      <div style={{ width: '100%' }}>
                        <ParamEditControls>
                          <ParamEditInput type="number" min="0" value={editDraft['footprint'] ?? formData.buildingFootprintArea}
                            onChange={e => setEditDraft(p => ({ ...p, footprint: e.target.value }))} autoFocus />
                          <ParamSaveBtn
                            disabled={!(editDraft['footprint'] ?? formData.buildingFootprintArea ?? '').toString().trim()}
                            onClick={() => { setFormData(p => ({ ...p, buildingFootprintArea: editDraft['footprint'] ?? p.buildingFootprintArea })); setEditingField(null); }}>Save</ParamSaveBtn>
                          <ParamCancelBtn onClick={cancelEdit}>Cancel</ParamCancelBtn>
                        </ParamEditControls>
                        {!(editDraft['footprint'] ?? formData.buildingFootprintArea ?? '').toString().trim() && (
                          <FieldError>Building footprint area can't be empty.</FieldError>
                        )}
                      </div>
                    ),
                    true
                  )}
                  {!(isAreaSplit
                    ? ((formData.buildingFootprintAreaExisting ?? '').toString().trim() || (formData.buildingFootprintAreaProposed ?? '').toString().trim())
                    : (formData.buildingFootprintArea ?? '').toString().trim()) && (
                    <FieldError style={{ padding: '0 0.75rem' }}>Building footprint area is required.</FieldError>
                  )}

                  {/* Gross Floor Area */}
                  {renderPencilRow(
                    'Gross Floor Area', 'gfa', formData.grossFloorArea,
                    isAreaSplit
                      ? <ParamValue>Existing {formData.grossFloorAreaExisting || '0'} + Proposed {formData.grossFloorAreaProposed || '0'} = <strong>{String(Math.round(((parseFloat(formData.grossFloorAreaExisting||'')||0)+(parseFloat(formData.grossFloorAreaProposed||'')||0))*100)/100)} {areaLabel}</strong></ParamValue>
                      : <ParamValue $missing={!formData.grossFloorArea}>{formData.grossFloorArea ? `${formatWithCommas(formData.grossFloorArea)} ${areaLabel}` : '—'}</ParamValue>,
                    isAreaSplit ? (
                      <ParamEditControls $stacked>
                        <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
                          <div style={{ flex: 1 }}><SubLabel>Existing</SubLabel>
                            <ParamEditInput type="number" min="0" value={editDraft['gfaExisting'] ?? formData.grossFloorAreaExisting}
                              onChange={e => setEditDraft(p => ({ ...p, gfaExisting: e.target.value }))} autoFocus /></div>
                          <div style={{ flex: 1 }}><SubLabel>Proposed</SubLabel>
                            <ParamEditInput type="number" min="0" value={editDraft['gfaProposed'] ?? formData.grossFloorAreaProposed}
                              onChange={e => setEditDraft(p => ({ ...p, gfaProposed: e.target.value }))} /></div>
                        </div>
                        <ParamEditActions>
                          <ParamSaveBtn onClick={() => { setFormData(p => ({ ...p, grossFloorAreaExisting: editDraft['gfaExisting'] ?? p.grossFloorAreaExisting, grossFloorAreaProposed: editDraft['gfaProposed'] ?? p.grossFloorAreaProposed })); setEditingField(null); }}>Save</ParamSaveBtn>
                          <ParamCancelBtn onClick={cancelEdit}>Cancel</ParamCancelBtn>
                        </ParamEditActions>
                      </ParamEditControls>
                    ) : (
                      <ParamEditControls>
                        <ParamEditInput type="number" min="0" value={editDraft['gfa'] ?? formData.grossFloorArea}
                          onChange={e => setEditDraft(p => ({ ...p, gfa: e.target.value }))} autoFocus />
                        <ParamSaveBtn onClick={() => { setFormData(p => ({ ...p, grossFloorArea: editDraft['gfa'] ?? p.grossFloorArea })); setEditingField(null); }}>Save</ParamSaveBtn>
                        <ParamCancelBtn onClick={cancelEdit}>Cancel</ParamCancelBtn>
                      </ParamEditControls>
                    )
                  )}

                  {renderPencilRow(
                    'Building Height', 'buildingHeight', formData.buildingHeight,
                    <ParamValue $missing={!formData.buildingHeight}>{formData.buildingHeight ? `${formData.buildingHeight} ${linearLabel}` : '—'}</ParamValue>,
                    <ParamEditControls>
                      <ParamEditInput type="number" min="0" step="0.1" value={editDraft['buildingHeight'] ?? formData.buildingHeight}
                        onChange={e => setEditDraft(p => ({ ...p, buildingHeight: e.target.value }))} autoFocus />
                      <ParamSaveBtn onClick={() => saveEditNumber('buildingHeight', 'buildingHeight')}>Save</ParamSaveBtn>
                      <ParamCancelBtn onClick={cancelEdit}>Cancel</ParamCancelBtn>
                    </ParamEditControls>
                  )}

                  {/* High Building — toggle, no pencil */}
                  <ParamRow>
                    <ParamLabel>High Building</ParamLabel>
                    <ToggleGroup
                      value={formData.highBuilding || 'no'}
                      options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
                      onChange={v => setFormData(p => ({ ...p, highBuilding: v }))}
                    />
                  </ParamRow>
                </ParamRows>
              </SummarySection>

              {/* Compliance Context */}
              <SummarySection>
                <SumSectionLabel>Compliance Context</SumSectionLabel>
                <ParamRows>

                  {/* Construction Type */}
                  {renderPencilRow(
                    'Construction Type', 'constructionType', formData.constructionType,
                    formData.constructionType
                      ? <ParamValue>{constructionTypeTitleForSummary}</ParamValue>
                      : <ParamWarnValue>Not set — extraction will run unfiltered</ParamWarnValue>,
                    <ParamEditControls $stacked>
                      <SelectField value={editDraft['constructionType'] ?? formData.constructionType}
                        onChange={e => setEditDraft(p => ({ ...p, constructionType: e.target.value }))}
                        autoFocus>
                        <option value="">Select type</option>
                        {CONSTRUCTION_LIST.map(([key, title]) => <option key={key} value={key}>{title}</option>)}
                      </SelectField>
                      <ParamEditActions>
                        <ParamSaveBtn onClick={() => saveEditDropdown('constructionType', 'constructionType')}>Save</ParamSaveBtn>
                        <ParamCancelBtn onClick={cancelEdit}>Cancel</ParamCancelBtn>
                      </ParamEditActions>
                    </ParamEditControls>,
                    true
                  )}
                  {!formData.constructionType && (
                    <FieldError style={{ padding: '0 0.75rem' }}>
                      Construction type is required. Set it before creating the project.
                    </FieldError>
                  )}

                  {/* Sprinkler System */}
                  {renderPencilRow(
                    'Sprinkler System', 'sprinklerSystem', formData.sprinklerSystem, 
                    <ParamValue $missing={!formData.sprinklerSystem}>{sprinklerLabelForSummary}</ParamValue>,
                    <ParamEditControls>
                      <SelectField value={editDraft['sprinklerSystem'] ?? formData.sprinklerSystem}
                        onChange={e => setEditDraft(p => ({ ...p, sprinklerSystem: e.target.value }))}
                        style={{ flex: 1 }} autoFocus>
                        {SPRINKLER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </SelectField>
                      <ParamSaveBtn onClick={() => saveEditDropdown('sprinklerSystem', 'sprinklerSystem')}>Save</ParamSaveBtn>
                      <ParamCancelBtn onClick={cancelEdit}>Cancel</ParamCancelBtn>
                    </ParamEditControls>
                  )}

                  {/* Fire Alarm — toggle */}
                  <ParamRow>
                    <ParamLabel>Fire Alarm</ParamLabel>
                    <ToggleGroup
                      value={formData.fireAlarm || 'unknown'}
                      options={YES_NO_UNKNOWN_OPTIONS}
                      onChange={v => setFormData(p => ({ ...p, fireAlarm: v }))}
                    />
                  </ParamRow>

                  {/* Standpipe — toggle */}
                  <ParamRow>
                    <ParamLabel>Standpipe</ParamLabel>
                    <ToggleGroup
                      value={formData.standpipe || 'unknown'}
                      options={YES_NO_UNKNOWN_OPTIONS}
                      onChange={v => setFormData(p => ({ ...p, standpipe: v }))}
                    />
                  </ParamRow>

                  {/* Hazardous Substances — toggle */}
                  <ParamRow>
                    <ParamLabel>Hazardous Substances</ParamLabel>
                    <ToggleGroup
                      value={formData.hazardousSubstances || 'no'}
                      options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
                      onChange={v => setFormData(p => ({ ...p, hazardousSubstances: v }))}
                    />
                  </ParamRow>

                  {/* Budget */}
                  {renderPencilRow(
                    'Rough Construction Budget', 'roughConstructionBudget', formData.roughConstructionBudget,
                    <ParamValue $missing={!formData.roughConstructionBudget}>
                      {formData.roughConstructionBudget ? `$${formatWithCommas(formData.roughConstructionBudget)}` : '—'}
                    </ParamValue>,
                    <ParamEditControls>
                      <ParamEditInput type="text"
                        value={editDraft['roughConstructionBudget'] ?? formatWithCommas(formData.roughConstructionBudget)}
                        onChange={e => { const r = removeCommas(e.target.value); if (!/^\d*$/.test(r)) return; setEditDraft(p => ({ ...p, roughConstructionBudget: r })); }}
                        placeholder="e.g. 2,500,000" autoFocus />
                      <ParamSaveBtn onClick={() => { setFormData(p => ({ ...p, roughConstructionBudget: editDraft['roughConstructionBudget'] ?? p.roughConstructionBudget })); setEditingField(null); }}>Save</ParamSaveBtn>
                      <ParamCancelBtn onClick={cancelEdit}>Cancel</ParamCancelBtn>
                    </ParamEditControls>
                  )}
                </ParamRows>
              </SummarySection>
              </>
              )}
                </>
              )}

              {/* Right-side occupancy popup */}
              {occRightOpen && (
                <OccPopupRight ref={occRightRef} style={{ left: (() => { const r = wizardRef.current?.getBoundingClientRect(); return r ? `${r.right + 12}px` : '50%'; })() }}>
                  <OccPopupHeader>
                    <OccPopupTitle>Edit Occupancy Types</OccPopupTitle>
                    <OccPopupSubtitle>Select all that apply</OccPopupSubtitle>
                  </OccPopupHeader>
                  <OccSearchWrap>
                    <OccSearchInput type="text" placeholder="Search occupancy types…"
                      value={searchQueryRight} onChange={e => setSearchQueryRight(e.target.value)} autoFocus />
                  </OccSearchWrap>
                  <OccList>
                    {filteredOccupanciesRight.map(([key, title, desc, code]) => {
                      const isSelected = formData.types.includes(key);
                      return (
                        <OccOption key={key} $selected={isSelected} onClick={() => handleTypeSelect(key, code)}>
                          <OccCheckbox $checked={isSelected}>{isSelected ? '✓' : ''}</OccCheckbox>
                          <div>
                            <OccOptionTitle>{title} <OccOptionCode>({code.toUpperCase()})</OccOptionCode></OccOptionTitle>
                            <OccOptionDesc>{desc}</OccOptionDesc>
                          </div>
                        </OccOption>
                      );
                    })}
                  </OccList>
                  <OccPopupFooter>
                    <OccCount>{formData.types.length} selected</OccCount>
                    <OccDoneBtn onClick={() => setOccRightOpen(false)}>Done</OccDoneBtn>
                  </OccPopupFooter>
                </OccPopupRight>
              )}
            </>
          )}

          {/* ── STEP 6: Congrats ── */}
          {step === 6 && (
            <CongratulatoryMessage>
              <CongratulatoryIconRing>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </CongratulatoryIconRing>
              <CongratulatoryTitle>Project Created!</CongratulatoryTitle>
              <CongratulatoryBody>
                <strong>{formData.name || 'Your project'}</strong> is ready. Start by linking a folder to begin indexing your project documents.
              </CongratulatoryBody>
            </CongratulatoryMessage>
          )}

        </WizardBody>

        {/* Footer */}
        <WizardFooter>
          <div>
            {step > 1 && step < 6 && (
              <WizardBtn $ghost type="button" onClick={(e) => (e.preventDefault(), handleBack())}>← Back</WizardBtn>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.625rem', alignItems: 'center' }}>
            {step === 4 && isFieldVisible(categoryProfile, 'constructionType') && <span style={{ color: '#bbb', fontSize: '0.75rem', fontFamily: T.font }}>Construction Type is required on this step</span>}
            {multiBuildingIncomplete && <span style={{ color: '#d32f2f', fontSize: '0.75rem', fontFamily: T.font }}>Complete required fields for all buildings to continue</span>}
            {step < 5 ? (
              <WizardBtn $primary type="button" disabled={isNextDisabled}
                onClick={(e) => (e.preventDefault(), handleNext())}>Next →</WizardBtn>
            ) : step === 5 ? (
              <WizardBtn $primary type="submit" disabled={isSubmitting || step5MissingRequired}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleSubmit(e); }}>
                {isSubmitting ? t('project.creatingProject', 'Creating Project…') : t('project.createProject', 'Create Project')}
              </WizardBtn>
            ) : (
              <WizardBtn $primary type="button" onClick={onClose}>{t('project.goToProject', 'Go to Project →')}</WizardBtn>
            )}
          </div>
        </WizardFooter>

      </DialogContent>
    </DialogOverlay>
  );
};

export default ProjectWizard;